import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Tag, Scale, Camera, Search, Plus, Radio, CheckCircle2, AlertTriangle, Download, X, Loader2,
  ArrowLeft, Undo2, Image as ImageIcon, ChevronLeft, ChevronRight, ExternalLink,
  ShieldCheck, XCircle, MinusCircle, Flag, Shuffle, Send, Pencil, Clock, RotateCcw,
  ClipboardList, Save, Settings, ClipboardCheck, Copy,
} from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import StatCard from '../../components/StatCard'
import DataLoadError from '../../components/DataLoadError'
import { formatEAT, kampalaToday } from '../../components/SmartKraalWeighings'
import { variantUrl, fallbackToOriginal } from '../../utils/media'
import { useAuth } from '../../contexts/AuthContext'
import { canReviewRegistration } from '../../lib/permissions'
import {
  BINDING_STATUS, BIND_METHOD_LABEL, CHECK_RULE_LABEL, CHECK_RESULT, BindingStatusChip as StatusChip, eidForm, EID_FORM_LABEL,
} from '../../components/SmartKraalBindingChip'
import AssignEidModal from '../../components/AssignEidModal'
import {
  getSmartKraalRegistrationCounts, getSmartKraalRegistrationQueue, getSmartKraalTagRegistry,
  bindSmartKraalTag, bindSmartKraalTagByCrushEntry, createAnimalFromSmartKraalTag, retireSmartKraalTagLink,
  confirmSmartKraalTagLink, sendBackSmartKraalTagLink,
  getSmartKraalIncompleteProfiles, completeSmartKraalProfiles, getOperatingProfile,
  getSmartKraalRegistrationAcceptance,
  smartKraalTagLinksCsvUrl, getCattle, lookupByRfid, getBreeds, getOwnerSegments,
} from '../../services/api'

/**
 * Registration at the crush (plan smartkraal-crush-registration, Phases 2 + 3).
 *
 * One SmartKraal tag id = one card. The kraal tells us what it saw (passes,
 * weights, stills, farm number); the farm tells us which animal wears the
 * tag. Two ways to say it: confirm/search/create from the photo queue, or
 * type EID + tag id on tagging day. Every bind promotes that tag's weights
 * to the animal at once and runs the plausibility checks for the tag.
 *
 * Review (Phase 3): the assurance officer / system admin sees "Awaiting
 * review" as cards (photos, profile, last weights, checks with reasons) and
 * confirms or sends back with a note. The farm sees sent-back cards with the
 * note and answers: Resubmit (same pair), Re-bind (another animal), Edit
 * profile, or Retire.
 *
 * Census mode (Phase 4b-2): the Tagging day tab as the onboarding census tool.
 * The pod registers each animal at the race as it is tagged, before the kraal
 * has read anything: tag number, EID if it wears one, a short profile with
 * sticky values, Enter advances. Animals are dated from the operating
 * profile's herd baseline date (opening stock, not purchases). The Profiles
 * tab fills the blanks afterwards, in bulk, never overwriting a value; a
 * placeholder electronic id is swapped for the stick-read EID explicitly.
 */

const STATUS = BINDING_STATUS
const METHOD = BIND_METHOD_LABEL
const TABS = [
  { key: 'unbound', label: 'Unbound tags', status: 'unbound', count: c => c?.unbound },
  { key: 'tagging', label: 'Tagging day' },
  { key: 'profiles', label: 'Profiles', count: c => c?.profile_incomplete },
  { key: 'review', label: 'Awaiting review', status: 'bound', count: c => c?.bound },
  { key: 'sent_back', label: 'Sent back', status: 'sent_back', count: c => c?.sent_back },
  { key: 'confirmed', label: 'Confirmed', status: 'confirmed', count: c => c?.confirmed },
  { key: 'all', label: 'All tags', status: 'all', count: c => c?.seen },
  { key: 'onboarding', label: 'Onboarding' },
]
const PAGE = 48
const PROFILE_PAGE = 100
const CENSUS_KEY = (ranchId) => `wb.census.${ranchId}`
const STICKY_KEY = (ranchId) => `wb.census.sticky.${ranchId}`
const STICKY_DEFAULTS = { sex: 'female', acquisition_type: 'purchased', breed_id: '', owner_segment_id: '', owner_segment: '', birth_year: '' }
const TAG_RE = /^\d{1,12}$/
const THIS_YEAR = new Date().getFullYear()
const yearValid = (y) => { const n = parseInt(y, 10); return Number.isFinite(n) && n >= 1980 && n <= THIS_YEAR }
// A year-only estimate is stored as 1 July of that year; for the current year that can be a
// future date before July, so it clamps to today (Kampala, UTC+3, no DST).
const yearToDate = (y) => {
  const n = parseInt(y, 10)
  const today = new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10)
  const first = `${n}-07-01`
  return first > today ? today : first
}

const errDetail = (err) => {
  const d = err?.response?.data?.detail
  const s = typeof d === 'string' ? d : (d ? JSON.stringify(d) : (err?.message || 'Something went wrong'))
  return s.replace(/^[a-z_]+:\s*/, '')
}
const errCode = (err) => {
  const d = err?.response?.data?.detail
  const m = typeof d === 'string' ? d.match(/^([a-z_]+):/) : null
  return m ? m[1] : null
}
const kg = (v) => (v == null ? '—' : `${Number(v).toFixed(Number(v) % 1 ? 1 : 0)} kg`)
const animalName = (a) => a?.local_name || a?.tag_id || a?.rfid_tag || 'animal'
const shortEAT = (iso) => (iso ? formatEAT(iso).replace(/,? \d{2}:\d{2} EAT$/, '') : '—')

function useDebounced(value, ms) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------
// Stills come from weighing sessions only: a tag the kraal has read at the
// census but never weighed has none by construction, and some weighing sessions
// carry no still on the SmartKraal side either. `weighed` picks the honest line.
function Stills({ media, compact = false, weighed = true }) {
  const h = compact ? 'h-20' : 'h-36'
  if (!media || (!media.cam1_still && !media.cam2_still)) {
    return (
      <div className={`${h} rounded-lg bg-earth-50 border border-dashed border-earth-200 flex flex-col items-center justify-center text-earth-400 text-xs text-center px-2`}>
        <ImageIcon className="h-5 w-5 mb-1" />
        {weighed ? 'No photo yet' : 'Not weighed yet'}
        {!compact && <span>{weighed ? 'the kraal recorded no still for this tag’s weighings so far' : 'stills come from weighing sessions'}</span>}
      </div>
    )
  }
  const shots = [['cam1_still', 'Camera 1'], ['cam2_still', 'Camera 2']].filter(([k]) => media[k])
  // The card loads the 480 px thumb (mig 138; ~25 KB, cached a week) and falls
  // back to the original while the nightly pass has not built it yet; the link
  // opens the original.
  return (
    <div className={`grid ${shots.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
      {shots.map(([k, title]) => (
        <a key={k} href={media[k]} target="_blank" rel="noreferrer" title={`${title} · ${formatEAT(media.session_ts_utc)}`}>
          <img src={media.urls?.[k]?.thumb || variantUrl(media[k], 'thumb')} alt={title}
            className={`${h} w-full object-cover rounded-lg bg-earth-100`} loading="lazy" decoding="async"
            onError={fallbackToOriginal(media[k])} />
        </a>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Unbound tag card
// ---------------------------------------------------------------------------
function TagCard({ card, canWrite, busy, onConfirm, onFind, onNew }) {
  const p = card.proposal
  return (
    <div className="card p-4 flex flex-col gap-3" data-testid={`tag-card-${card.p_idtag}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-earth-500">SmartKraal tag</div>
          <div className="text-3xl font-bold text-primary-900 leading-tight">{card.p_idtag}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {!card.seen_by_kraal && (
            <span className="rounded-full bg-earth-100 px-2 py-0.5 text-xs text-earth-600">not read yet</span>
          )}
          {card.c_num_current && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700" title={`Farm number on the SmartKraal side: ${card.c_num_current}`}>
              farm no. {card.c_num_current.slice(0, 8)}…
            </span>
          )}
        </div>
      </div>

      <Stills media={card.media} weighed={card.n_weighings > 0} />

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-earth-500">Last weight</dt>
        <dd className="text-right font-medium">
          {kg(card.last_weight_kg)}
          {card.last_confidence != null && <span className="ml-1 text-xs text-earth-500">conf {card.last_confidence}</span>}
        </dd>
        <dt className="text-earth-500">Passes</dt>
        <dd className="text-right">{card.n_weighings}{card.read_count ? ` · ${card.read_count} reads` : ''}</dd>
        <dt className="text-earth-500">First seen</dt>
        <dd className="text-right">{shortEAT(card.first_seen_utc)}</dd>
        <dt className="text-earth-500">Last seen</dt>
        <dd className="text-right">{shortEAT(card.last_seen_utc)}</dd>
      </dl>

      {p && (
        <div className="rounded-lg bg-primary-50 border border-primary-100 p-3 text-sm">
          <div className="text-primary-900">
            Looks like <span className="font-semibold">{animalName(p.animal)}</span>
            <span className="text-earth-600"> · {p.animal.rfid_tag}{p.animal.category ? ` · ${p.animal.category}` : ''}</span>
          </div>
          <div className="text-xs text-earth-600 mt-0.5">
            {p.basis === 'tag_id' ? 'its visual tag number is this SmartKraal id' : 'the SmartKraal farm number is this animal'}
          </div>
          {canWrite && (
            <button type="button" disabled={busy} onClick={() => onConfirm(card)}
                    className="btn-primary mt-2 w-full" data-testid={`confirm-${card.p_idtag}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirm {animalName(p.animal)}
            </button>
          )}
        </div>
      )}
      {!p && card.tag_id_matches > 1 && (
        <div className="text-xs text-amber-700 flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5" /> {card.tag_id_matches} animals carry visual tag {card.p_idtag}; pick one.
        </div>
      )}

      {canWrite && (
        <div className="flex gap-2 mt-auto">
          <button type="button" onClick={() => onFind(card)} className="btn-secondary flex-1" disabled={busy}>
            <Search className="h-4 w-4 mr-2" /> Find animal
          </button>
          <button type="button" onClick={() => onNew(card)} className="btn-secondary flex-1" disabled={busy}>
            <Plus className="h-4 w-4 mr-2" /> New animal
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------
function Modal({ title, subtitle, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} my-4`}>
        <div className="flex items-start justify-between p-4 border-b border-earth-200">
          <div>
            <h2 className="font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-earth-500">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-earth-100 rounded-lg" aria-label="Close">
            <X className="h-5 w-5 text-earth-500" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Find an existing animal (search by EID / name / visual tag, or scan)
// ---------------------------------------------------------------------------
function AnimalPicker({ ranchId, card, busy, error, onClose, onPick, onNew }) {
  const [q, setQ] = useState('')
  const dq = useDebounced(q.trim(), 350)
  const inputRef = useRef(null)
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50) }, [])
  const res = useQuery({
    queryKey: ['regPickerSearch', ranchId, dq],
    queryFn: ({ signal }) => getCattle(ranchId, { search: dq, limit: 20, status: 'active' }, { signal }),
    enabled: !!ranchId && dq.length >= 2,
  })
  const items = res.data?.items || []
  return (
    <Modal title={`Bind SmartKraal tag ${card.p_idtag}`} subtitle="Scan the EID or type a name, EID or visual tag number" onClose={onClose}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="col-span-1"><Stills media={card.media} compact weighed={card.n_weighings > 0} /></div>
        <div className="col-span-2 text-sm text-earth-600">
          Last weight <span className="font-medium text-gray-900">{kg(card.last_weight_kg)}</span> · {card.n_weighings} passes
          · last seen {shortEAT(card.last_seen_utc)}
        </div>
      </div>
      <div className="relative">
        <Radio className="h-4 w-4 absolute left-3 top-3 text-earth-400" />
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Scan or type…"
               className="input pl-9 font-mono" autoComplete="off" data-testid="picker-search" />
      </div>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
      <ul className="mt-3 divide-y divide-earth-100 max-h-80 overflow-y-auto">
        {res.isFetching && <li className="py-3 text-sm text-earth-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</li>}
        {!res.isFetching && dq.length >= 2 && items.length === 0 && (
          <li className="py-3 text-sm text-earth-500">No active animal matches “{dq}”.</li>
        )}
        {items.map(a => (
          <li key={a.id} className="py-2 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-earth-100 overflow-hidden flex-shrink-0">
              {a.photo_url && <img src={variantUrl(a.photo_url, 'thumb')} alt="" className="h-10 w-10 object-cover" loading="lazy" onError={fallbackToOriginal(a.photo_url)} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">{animalName(a)}</div>
              <div className="text-xs text-earth-500 truncate">
                {a.rfid_tag}{a.tag_id ? ` · tag ${a.tag_id}` : ''} · {a.category}{a.owner_segment ? ` · ${a.owner_segment}` : ''}
                {a.smartkraal_p_idtag ? ` · already wears SK ${a.smartkraal_p_idtag}` : ''}
              </div>
            </div>
            <button type="button" className="btn-primary" disabled={busy || !!a.smartkraal_p_idtag}
                    onClick={() => onPick(a)} data-testid={`pick-${a.id}`}>
              Bind
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t border-earth-100 flex justify-between items-center text-sm">
        <span className="text-earth-500">Not in the register?</span>
        <button type="button" className="btn-secondary" onClick={onNew}><Plus className="h-4 w-4 mr-2" /> New animal from this tag</button>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// New animal from the SmartKraal record (D15.5)
// ---------------------------------------------------------------------------
function NewAnimalForm({ ranchId, card, pIdtag, rfid, busy, error, onClose, onSubmit }) {
  const [form, setForm] = useState({
    p_idtag: pIdtag || card?.p_idtag || '', rfid_tag: rfid || '', sex: '', acquisition_type: 'purchased',
    breed_id: '', owner_segment_id: '', owner_segment: '', birth_date: '', dob_precision: 'year',
    local_name: '', color_markings: '', notes: '',
  })
  const [otherOwner, setOtherOwner] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const { data: breeds } = useQuery({ queryKey: ['breeds'], queryFn: getBreeds })
  const { data: owners } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }), enabled: !!ranchId,
  })
  const ig = card?.igqabi
  const submit = (e) => {
    e.preventDefault()
    const body = {
      p_idtag: Number(form.p_idtag), sex: form.sex, acquisition_type: form.acquisition_type,
      rfid_tag: form.rfid_tag.trim() || null, breed_id: form.breed_id || null,
      owner_segment_id: otherOwner ? null : (form.owner_segment_id || null),
      owner_segment: otherOwner ? (form.owner_segment.trim() || null) : null,
      birth_date: form.birth_date || null, dob_precision: form.birth_date ? form.dob_precision : null,
      local_name: form.local_name.trim() || null, color_markings: form.color_markings.trim() || null,
      notes: form.notes.trim() || null,
      allow_unseen: !card?.seen_by_kraal,
      replace_sent_back: !!card?.replaceSentBack,   // "Re-bind…" from a sent-back card ended in "not in the register"
    }
    onSubmit(body)
  }
  return (
    <Modal title="New animal from the crush record" wide onClose={onClose}
           subtitle="The kraal has weighed this tag; the register has no animal for it yet. Fill what you know; the photo and ids come from the SmartKraal.">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 space-y-3">
          <Stills media={card?.media} weighed={!card || card.n_weighings > 0} />
          {card && (
            <div className="text-xs text-earth-600 space-y-0.5">
              <div>Last weight <span className="font-medium text-gray-900">{kg(card.last_weight_kg)}</span> (conf {card.last_confidence ?? '—'})</div>
              <div>{card.n_weighings} passes · first seen {shortEAT(card.first_seen_utc)}</div>
              {card.epc && <div className="font-mono break-all">EPC {card.epc}</div>}
              {ig && (ig.c_gen || ig.c_year) && (
                <div className="text-earth-500">Igqabi record: {ig.c_gen ? `sex ${ig.c_gen}` : ''}{ig.c_year ? ` · born ${[ig.c_day, ig.c_month, ig.c_year].filter(Boolean).join('/')}` : ''}</div>
              )}
            </div>
          )}
          <p className="text-xs text-earth-500">
            Electronic id: the LF EID if you enter one, else the tag's UHF EPC, else <span className="font-mono">SK-farm-id</span>.
          </p>
        </div>
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">SmartKraal tag id *</label>
            <input className="input font-mono" inputMode="numeric" required value={form.p_idtag}
                   readOnly={!!card} onChange={e => set('p_idtag', e.target.value.replace(/\D/g, ''))} data-testid="new-p-idtag" />
          </div>
          <div>
            <label className="label">LF EID (if it wears one)</label>
            <input className="input font-mono" value={form.rfid_tag} onChange={e => set('rfid_tag', e.target.value)} placeholder="982…" data-testid="new-rfid" />
          </div>
          <div>
            <label className="label">Sex *</label>
            <select className="input" required value={form.sex} onChange={e => set('sex', e.target.value)} data-testid="new-sex">
              <option value="">Select sex</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </div>
          <div>
            <label className="label">Acquisition *</label>
            <select className="input" required value={form.acquisition_type} onChange={e => set('acquisition_type', e.target.value)} data-testid="new-acq">
              <option value="purchased">Purchased</option>
              <option value="born_on_farm">Born on farm</option>
              <option value="gift">Gift</option>
              <option value="inherited">Inherited</option>
              <option value="exchange">Exchange</option>
            </select>
          </div>
          <div>
            <label className="label">Breed</label>
            <select className="input" value={form.breed_id} onChange={e => set('breed_id', e.target.value)}>
              <option value="">Unknown</option>
              {(breeds || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Owner / segment</label>
            <select className="input" value={otherOwner ? '__other__' : form.owner_segment_id}
                    onChange={e => {
                      const v = e.target.value
                      if (v === '__other__') { setOtherOwner(true); set('owner_segment_id', '') }
                      else { setOtherOwner(false); set('owner_segment_id', v) }
                    }}>
              <option value="">Select owner</option>
              {(owners || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              <option value="__other__">Other (add new)…</option>
            </select>
            {otherOwner && (
              <input className="input mt-2" value={form.owner_segment} onChange={e => set('owner_segment', e.target.value)} placeholder="New owner name" autoFocus />
            )}
          </div>
          <div>
            <label className="label">Birth date (estimate is fine)</label>
            <div className="flex gap-2">
              <input type="date" className="input" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} />
              <select className="input w-28" value={form.dob_precision} onChange={e => set('dob_precision', e.target.value)} disabled={!form.birth_date}>
                <option value="day">exact</option>
                <option value="month">month</option>
                <option value="year">year</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label" htmlFor="new-local-name">Name (optional)</label>
            <input id="new-local-name" className="input" value={form.local_name} onChange={e => set('local_name', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Colour / markings</label>
            <input className="input" value={form.color_markings} onChange={e => set('color_markings', e.target.value)} placeholder="e.g. red with white face, left horn broken" />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          {error && <p className="sm:col-span-2 text-sm text-red-700">{error}</p>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy || !form.p_idtag || !form.sex} data-testid="new-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Register and bind
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Retire a binding
// ---------------------------------------------------------------------------
function RetireForm({ row, busy, error, onClose, onSubmit }) {
  const [mode, setMode] = useState('lost_or_replaced')
  const [reason, setReason] = useState('')
  return (
    <Modal title={`Retire the binding of tag ${row.p_idtag}`} subtitle={`${animalName(row.animal)} · ${row.animal?.rfid_tag || ''}`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); onSubmit({ mode, reason: reason.trim() }) }} className="space-y-3 text-sm">
        <label className="flex gap-3 p-3 rounded-lg border border-earth-200 cursor-pointer">
          <input type="radio" name="mode" checked={mode === 'lost_or_replaced'} onChange={() => setMode('lost_or_replaced')} className="mt-1" />
          <span><span className="font-medium">Tag lost or replaced.</span> Past weights stay with this animal; the number is free for the next animal from today.</span>
        </label>
        <label className="flex gap-3 p-3 rounded-lg border border-earth-200 cursor-pointer">
          <input type="radio" name="mode" checked={mode === 'wrong_animal'} onChange={() => setMode('wrong_animal')} className="mt-1" />
          <span><span className="font-medium">Wrong animal.</span> This binding never held: its SmartKraal weights are removed from this animal and the whole history goes to the animal you bind next.</span>
        </label>
        <div>
          <label className="label">Reason *</label>
          <input className="input" required minLength={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. tag fell off in the dip; photo shows a steer" />
        </div>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-danger" disabled={busy || reason.trim().length < 2}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Undo2 className="h-4 w-4 mr-2" />} Retire binding
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Tagging day: EID + SmartKraal id, one pass per animal (the herd is already
// in the register; census mode below is for a herd that is not)
// ---------------------------------------------------------------------------
function CrushEntryForm({ ranchId, canWrite, onBound, onNewAnimal }) {
  const [eid, setEid] = useState('')
  const [idText, setIdText] = useState('')
  const [lookup, setLookup] = useState({ state: 'idle' })   // idle | loading | found | not_found | error
  const [log, setLog] = useState([])
  const [error, setError] = useState('')
  const eidRef = useRef(null)
  const idRef = useRef(null)
  const dEid = useDebounced(eid.trim(), 500)
  const dId = useDebounced(idText.trim(), 400)
  useEffect(() => { setTimeout(() => eidRef.current?.focus(), 50) }, [])

  const doLookup = async (tag) => {
    const t = (tag || '').trim()
    if (t.length < 3) { setLookup({ state: 'error', message: 'Scan or type the EID first.' }); return }
    setLookup({ state: 'loading' })
    try {
      const r = await lookupByRfid(t, ranchId)
      setLookup(r.found ? { state: 'found', animal: r.animal } : { state: 'not_found' })
      if (r.found) setTimeout(() => idRef.current?.focus(), 30)
    } catch (err) {
      setLookup({ state: 'error', message: errDetail(err) })
    }
  }
  useEffect(() => { if (dEid.length >= 10 && lookup.state === 'idle') doLookup(dEid) }, [dEid])   // reader finished typing

  const registry = useQuery({
    queryKey: ['smartkraalTagRegistry', ranchId, dId],
    queryFn: ({ signal }) => getSmartKraalTagRegistry(ranchId, dId, { signal }),
    enabled: !!ranchId && /^\d{1,12}$/.test(dId),
  })
  const reg = /^\d{1,12}$/.test(dId) ? registry.data : null
  const regBound = reg && reg.link && ['bound', 'confirmed', 'sent_back'].includes(reg.status)

  const bind = useMutation({
    mutationFn: (body) => bindSmartKraalTagByCrushEntry(ranchId, body),
    onSuccess: (out, body) => {
      setLog(l => [{ at: new Date(), eid: body.rfid_tag, p_idtag: body.p_idtag, animal: out.animal,
                     promoted: out.promotion?.promoted ?? 0, promoError: out.promotion?.error || null,
                     already: out.already_bound, rebound: out.rebound, warnings: out.warnings || [] }, ...l])
      onBound(out)
      reset()
    },
    onError: (err) => setError(errDetail(err)),
  })
  const reset = () => {
    setEid(''); setIdText(''); setLookup({ state: 'idle' }); setError('')
    setTimeout(() => eidRef.current?.focus(), 30)
  }
  const canBind = canWrite && lookup.state === 'found' && /^\d{1,12}$/.test(idText.trim()) && !regBound && !bind.isPending
  const submit = (e) => {
    e?.preventDefault()
    if (!canBind) return
    setError('')
    bind.mutate({ rfid_tag: eid.trim(), p_idtag: Number(idText.trim()), allow_unseen: true })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <form onSubmit={submit} className="lg:col-span-3 card p-5 space-y-4">
        <div>
          <label className="label flex items-center gap-2"><Radio className="h-4 w-4 text-primary-600" /> 1. Scan or type the EID (LF tag)</label>
          <input ref={eidRef} value={eid} data-testid="td-eid"
                 onChange={e => { setEid(e.target.value); if (lookup.state !== 'idle') setLookup({ state: 'idle' }) }}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); doLookup(eid) } }}
                 className="input text-lg font-mono tracking-wider py-3" autoComplete="off" autoCorrect="off" spellCheck="false"
                 placeholder="982…" inputMode="numeric" />
          <div className="mt-2 min-h-[2.5rem] text-sm" data-testid="td-animal">
            {lookup.state === 'loading' && <span className="text-earth-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Looking up…</span>}
            {lookup.state === 'found' && (
              <div className="rounded-lg bg-primary-50 border border-primary-100 p-3 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-primary-900">{animalName(lookup.animal)}</div>
                  <div className="text-xs text-earth-600">
                    {lookup.animal.category} · {lookup.animal.sex}{lookup.animal.breed_name ? ` · ${lookup.animal.breed_name}` : ''}
                    {lookup.animal.last_weight_kg ? ` · ${kg(lookup.animal.last_weight_kg)}` : ''}
                    {lookup.animal.tag_id ? ` · visual tag ${lookup.animal.tag_id}` : ''}
                  </div>
                </div>
              </div>
            )}
            {lookup.state === 'not_found' && (
              <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 flex items-center justify-between gap-3">
                <span className="text-amber-900">No animal on this ranch wears EID <span className="font-mono">{eid.trim()}</span>.</span>
                {canWrite && (
                  <button type="button" className="btn-secondary whitespace-nowrap"
                          onClick={() => onNewAnimal({ rfid: eid.trim(), pIdtag: idText.trim() })}>
                    <Plus className="h-4 w-4 mr-1" /> New animal with this EID
                  </button>
                )}
              </div>
            )}
            {lookup.state === 'error' && <span className="text-red-700">{lookup.message}</span>}
          </div>
        </div>

        <div>
          <label className="label flex items-center gap-2"><Tag className="h-4 w-4 text-amber-600" /> 2. Number written on the SmartKraal tag</label>
          <input ref={idRef} value={idText} data-testid="td-id" inputMode="numeric"
                 onChange={e => setIdText(e.target.value.replace(/\D/g, ''))}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
                 className="input text-2xl font-bold py-3 w-48" autoComplete="off" placeholder="e.g. 612" />
          <div className="mt-2 min-h-[1.5rem] text-sm" data-testid="td-registry">
            {/^\d{1,12}$/.test(dId) && registry.isFetching && <span className="text-earth-500">Checking the kraal's registry…</span>}
            {dId.length > 12 && <span className="text-red-700">That is not a SmartKraal tag number (too long).</span>}
            {registry.error && !registry.isFetching && <span className="text-red-700">Could not check the registry: {errDetail(registry.error)}</span>}
            {reg && !registry.isFetching && (
              regBound ? (
                <span className="text-red-700 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />
                  Tag {reg.p_idtag} is already bound to {animalName(reg.animal)} ({STATUS[reg.status]?.label}). Retire that binding first.
                </span>
              ) : reg.known ? (
                <span className="text-primary-800">
                  Seen by the kraal: {reg.n_weighings} pass{reg.n_weighings === 1 ? '' : 'es'}
                  {reg.last_weight_kg != null ? `, last ${kg(reg.last_weight_kg)}` : ''}
                  {reg.last_seen_utc ? ` on ${shortEAT(reg.last_seen_utc)}` : ''}. Not bound yet.
                </span>
              ) : (
                <span className="text-earth-600">Not read by the kraal yet. Normal for a tag fitted today; its weights link from the next dip day.</span>
              )
            )}
          </div>
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}
        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary px-6 py-3 text-base" disabled={!canBind} data-testid="td-bind">
            {bind.isPending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Tag className="h-5 w-5 mr-2" />} Bind
          </button>
          <button type="button" className="btn-secondary" onClick={reset}>Clear</button>
          <span className="text-sm text-earth-500 ml-auto">{log.length} bound this session</span>
        </div>
      </form>

      <div className="lg:col-span-2 card p-5">
        <h3 className="font-medium text-primary-900 mb-2">Today at the crush</h3>
        {log.length === 0 ? (
          <p className="text-sm text-earth-500">Each bind appears here with the number of weights it linked. Enter advances from the EID field to the tag number and from there to Bind.</p>
        ) : (
          <ul className="divide-y divide-earth-100 text-sm max-h-[28rem] overflow-y-auto" data-testid="td-log">
            {log.map((l, i) => (
              <li key={i} className="py-2 flex items-center gap-3">
                <span className="text-2xl font-bold text-primary-900 w-16">{l.p_idtag}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{animalName(l.animal)}</div>
                  <div className="text-xs text-earth-500 font-mono">{l.eid}</div>
                </div>
                <div className="text-right text-xs text-earth-600">
                  <div>{l.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                  <div className={l.promoError ? 'text-amber-700' : ''}>
                    {l.already ? 'already bound' : l.promoError ? 'weights not linked yet (sync retries)' : l.rebound ? 're-asserted after send-back'
                      : l.promoted ? `${l.promoted} weight${l.promoted === 1 ? '' : 's'} linked` : 'no weights yet'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Census mode (Phase 4b-2): register the herd at the race, Enter-only
// ---------------------------------------------------------------------------
function loadSticky(ranchId) {
  try { return { ...STICKY_DEFAULTS, ...JSON.parse(localStorage.getItem(STICKY_KEY(ranchId)) || '{}') } } catch { return { ...STICKY_DEFAULTS } }
}

function CensusForm({ ranchId, canWrite, baseline, onRegistered, onBound }) {
  const [idText, setIdText] = useState('')
  const [eid, setEid] = useState('')
  const [lookup, setLookup] = useState({ state: 'idle' })   // idle | loading | found | not_found | none | error
  const [sticky, setStickyState] = useState(() => loadSticky(ranchId))
  const [otherOwner, setOtherOwner] = useState(false)
  const [log, setLog] = useState([])
  const [error, setError] = useState('')
  const refs = { id: useRef(null), eid: useRef(null), sex: useRef(null), acq: useRef(null), breed: useRef(null), owner: useRef(null), ownerOther: useRef(null), year: useRef(null), submit: useRef(null) }
  const dId = useDebounced(idText.trim(), 400)
  useEffect(() => { setTimeout(() => refs.id.current?.focus(), 50) }, [])
  const setSticky = (k, v) => setStickyState(st => {
    const next = { ...st, [k]: v }
    try { localStorage.setItem(STICKY_KEY(ranchId), JSON.stringify(next)) } catch { /* private mode */ }
    return next
  })
  const { data: breeds } = useQuery({ queryKey: ['breeds'], queryFn: getBreeds })
  const { data: owners } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }), enabled: !!ranchId,
  })
  const registry = useQuery({
    queryKey: ['smartkraalTagRegistry', ranchId, dId],
    queryFn: ({ signal }) => getSmartKraalTagRegistry(ranchId, dId, { signal }),
    enabled: !!ranchId && TAG_RE.test(dId),
  })
  // the registry answer is only trusted for the number in the field right now (the
  // debounce lags a fast operator; the server refuses a bound tag anyway)
  const reg = TAG_RE.test(dId) && dId === idText.trim() ? registry.data : null
  const regBound = reg && reg.link && ['bound', 'confirmed', 'sent_back'].includes(reg.status)
  // Focus moves synchronously: a stick reader "types" the EID in the same instant the
  // operator's Enter lands, and a deferred focus would put its first digits in the
  // tag-number field. Deferred only where the target is disabled until the next render.
  const focus = (k, defer = false) => {
    const el = refs[k].current
    if (!el) return
    if (defer) setTimeout(() => el.focus(), 20); else el.focus()
  }
  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn() } }

  // Enter on the EID field: empty = the animal wears no LF tag (placeholder id);
  // a known EID = the animal is already in the register, so the tag is BOUND to it
  // (as on an ordinary tagging day); an unknown EID = the new animal's electronic id.
  const checkEid = async () => {
    const t = eid.trim()
    if (!t) { setLookup({ state: 'none' }); focus('sex'); return }
    if (t.length < 3) { setLookup({ state: 'error', message: 'EID too short; leave it empty if the animal wears no LF tag.' }); return }
    setLookup({ state: 'loading' })
    try {
      const r = await lookupByRfid(t, ranchId)
      setLookup(r.found ? { state: 'found', animal: r.animal } : { state: 'not_found' })
      focus(r.found ? 'submit' : 'sex', r.found)
    } catch (err) {
      setLookup({ state: 'error', message: errDetail(err) })
    }
  }

  const done = (entry) => { setLog(l => [{ at: new Date(), ...entry }, ...l]); reset() }
  const create = useMutation({
    mutationFn: (body) => createAnimalFromSmartKraalTag(ranchId, body),
    onSuccess: (out, body) => {
      done({ p_idtag: body.p_idtag, eid: body.rfid_tag, animal: out.animal, created: true, rfid_source: out.rfid_source,
             acquisition_date: out.acquisition_date, warnings: out.warnings || [] })
      onRegistered(out)
    },
    onError: (err) => setError(errDetail(err) + (errCode(err) === 'rfid_exists' ? ' Enter that EID again and press Enter: the tag binds to the animal that wears it.' : '')),
  })
  const bind = useMutation({
    mutationFn: (body) => bindSmartKraalTagByCrushEntry(ranchId, body),
    onSuccess: (out, body) => {
      done({ p_idtag: body.p_idtag, eid: body.rfid_tag, animal: out.animal, created: false, already: out.already_bound,
             promoted: out.promotion?.promoted ?? 0, warnings: out.warnings || [] })
      onBound(out)
    },
    onError: (err) => setError(errDetail(err)),
  })
  const reset = () => { setIdText(''); setEid(''); setLookup({ state: 'idle' }); setError(''); focus('id', true) }
  const busy = create.isPending || bind.isPending
  const idOk = TAG_RE.test(idText.trim()) && !regBound
  const willBind = lookup.state === 'found'
  const yearOk = sticky.birth_year === '' || yearValid(sticky.birth_year)
  const canSubmit = canWrite && !!baseline && idOk && !busy && lookup.state !== 'loading'
    && (willBind || (!!sticky.sex && !!sticky.acquisition_type && yearOk && (!otherOwner || sticky.owner_segment.trim())))
  const submit = (e) => {
    e?.preventDefault()
    if (!canSubmit) return
    setError('')
    const p = Number(idText.trim())
    if (willBind) { bind.mutate({ rfid_tag: eid.trim(), p_idtag: p, allow_unseen: true }); return }
    const hasYear = sticky.birth_year !== '' && yearValid(sticky.birth_year)
    create.mutate({
      p_idtag: p, sex: sticky.sex, acquisition_type: sticky.acquisition_type, rfid_tag: eid.trim() || null,
      breed_id: sticky.breed_id || null,
      owner_segment_id: otherOwner ? null : (sticky.owner_segment_id || null),
      owner_segment: otherOwner ? (sticky.owner_segment.trim() || null) : null,
      birth_date: hasYear ? yearToDate(sticky.birth_year) : null, dob_precision: hasYear ? 'year' : null,
      census_baseline: true, allow_unseen: true,
    })
  }
  const registered = log.filter(l => l.created).length
  const bound = log.length - registered

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="census-form">
      <form onSubmit={submit} className="lg:col-span-3 card p-4 sm:p-5 space-y-4">
        <div>
          <label className="label flex items-center gap-2" htmlFor="td-c-id"><Tag className="h-4 w-4 text-amber-600" /> 1. Number written on the SmartKraal tag</label>
          <input id="td-c-id" ref={refs.id} value={idText} data-testid="td-c-id" inputMode="numeric" autoComplete="off" placeholder="e.g. 45"
                 onChange={e => setIdText(e.target.value.replace(/\D/g, ''))} onKeyDown={onEnter(() => { if (idOk) focus('eid') })}
                 className="input text-3xl font-bold py-3 w-full sm:w-56" />
          <div className="mt-1 min-h-[1.25rem] text-sm" data-testid="td-c-registry">
            {dId.length > 12 && <span className="text-red-700">That is not a SmartKraal tag number (too long).</span>}
            {reg && !registry.isFetching && (
              regBound ? (
                <span className="text-red-700 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />
                  Tag {reg.p_idtag} is already bound to {animalName(reg.animal)} ({STATUS[reg.status]?.label}). Check the number.
                </span>
              ) : reg.known ? (
                <span className="text-primary-800">Seen by the kraal already: {reg.n_weighings} pass{reg.n_weighings === 1 ? '' : 'es'}{reg.last_weight_kg != null ? `, last ${kg(reg.last_weight_kg)}` : ''}.</span>
              ) : <span className="text-earth-500">Not read by the kraal yet, as expected on census day.</span>
            )}
          </div>
        </div>

        <div>
          <label className="label flex items-center gap-2" htmlFor="td-c-eid"><Radio className="h-4 w-4 text-primary-600" /> 2. LF EID, if it wears one (Enter to skip)</label>
          <input id="td-c-eid" ref={refs.eid} value={eid} data-testid="td-c-eid" inputMode="numeric" autoComplete="off" autoCorrect="off" spellCheck="false" placeholder="982… or leave empty"
                 onChange={e => { setEid(e.target.value); if (lookup.state !== 'idle') setLookup({ state: 'idle' }) }} onKeyDown={onEnter(checkEid)}
                 className="input text-lg font-mono tracking-wider py-3" />
          <div className="mt-1 min-h-[1.25rem] text-sm" data-testid="td-c-animal">
            {lookup.state === 'loading' && <span className="text-earth-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Looking up…</span>}
            {lookup.state === 'none' && <span className="text-earth-600">No LF tag: the record gets a placeholder id until the stick reads one (Assign EID later).</span>}
            {lookup.state === 'not_found' && <span className="text-primary-800">New EID: it becomes this animal's electronic id.</span>}
            {lookup.state === 'found' && (
              <span className="text-primary-900 font-medium">Already in the register: {animalName(lookup.animal)}{lookup.animal.category ? ` (${lookup.animal.category})` : ''}. Enter binds tag {idText.trim()} to it.</span>
            )}
            {lookup.state === 'error' && <span className="text-red-700">{lookup.message}</span>}
          </div>
        </div>

        <fieldset disabled={willBind} className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${willBind ? 'opacity-40' : ''}`}>
          <legend className="label mb-2">3. Profile (sticks from the previous animal; Enter moves on)</legend>
          <div>
            <label className="label" htmlFor="td-c-sex">Sex *</label>
            <select id="td-c-sex" ref={refs.sex} className="input" value={sticky.sex} data-testid="td-c-sex"
                    onChange={e => setSticky('sex', e.target.value)} onKeyDown={onEnter(() => focus('acq'))}>
              <option value="female">Female</option><option value="male">Male</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="td-c-acq">Acquisition *</label>
            <select id="td-c-acq" ref={refs.acq} className="input" value={sticky.acquisition_type} data-testid="td-c-acq"
                    onChange={e => setSticky('acquisition_type', e.target.value)} onKeyDown={onEnter(() => focus('breed'))}>
              <option value="purchased">Purchased</option><option value="born_on_farm">Born on farm</option>
              <option value="gift">Gift</option><option value="inherited">Inherited</option><option value="exchange">Exchange</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="td-c-breed">Breed</label>
            <select id="td-c-breed" ref={refs.breed} className="input" value={sticky.breed_id} data-testid="td-c-breed"
                    onChange={e => setSticky('breed_id', e.target.value)} onKeyDown={onEnter(() => focus('owner'))}>
              <option value="">Unknown</option>
              {(breeds || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="td-c-owner">Owner</label>
            <select id="td-c-owner" ref={refs.owner} className="input" value={otherOwner ? '__other__' : sticky.owner_segment_id} data-testid="td-c-owner"
                    onChange={e => { const v = e.target.value; if (v === '__other__') { setOtherOwner(true); setSticky('owner_segment_id', '') } else { setOtherOwner(false); setSticky('owner_segment_id', v) } }}
                    onKeyDown={onEnter(() => focus(otherOwner ? 'ownerOther' : 'year'))}>
              <option value="">Unknown</option>
              {(owners || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              <option value="__other__">Other (add new)…</option>
            </select>
            {otherOwner && (
              <input ref={refs.ownerOther} className="input mt-2" value={sticky.owner_segment} onChange={e => setSticky('owner_segment', e.target.value)} placeholder="New owner name"
                     onKeyDown={onEnter(() => focus('year'))} data-testid="td-c-owner-other" />
            )}
          </div>
          <div>
            <label className="label" htmlFor="td-c-year">Born (year)</label>
            <input id="td-c-year" ref={refs.year} className={`input ${yearOk ? '' : 'border-red-400'}`} inputMode="numeric" placeholder="e.g. 2021" maxLength={4}
                   value={sticky.birth_year} data-testid="td-c-year"
                   onChange={e => setSticky('birth_year', e.target.value.replace(/\D/g, ''))} onKeyDown={onEnter(() => submit())} />
          </div>
        </fieldset>

        {error && <p className="text-sm text-red-700" data-testid="td-c-error">{error}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" ref={refs.submit} className="btn-primary px-6 py-3 text-base" disabled={!canSubmit} data-testid="td-c-submit">
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : willBind ? <Tag className="h-5 w-5 mr-2" /> : <Plus className="h-5 w-5 mr-2" />}
            {willBind ? 'Bind tag' : 'Register'}
          </button>
          <button type="button" className="btn-secondary" onClick={reset}>Clear</button>
          <span className="text-sm text-earth-500 ml-auto" data-testid="td-c-count">
            {registered} registered{bound ? `, ${bound} bound` : ''} this session
          </span>
        </div>
      </form>

      <div className="lg:col-span-2 card p-5">
        <h3 className="font-medium text-primary-900 mb-2">Today at the race</h3>
        {log.length === 0 ? (
          <p className="text-sm text-earth-500">
            Each animal appears here as it is registered. Tag number, Enter; EID or Enter; the profile sticks, so a run of similar animals
            is Enter, Enter, Enter. {baseline ? `All of them are on the farm since ${baseline}.` : ''}
          </p>
        ) : (
          <ul className="divide-y divide-earth-100 text-sm max-h-[28rem] overflow-y-auto" data-testid="td-c-log">
            {log.map((l, i) => (
              <li key={i} className="py-2 flex items-center gap-3">
                <span className="text-2xl font-bold text-primary-900 w-16">{l.p_idtag}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{animalName(l.animal)}{l.animal?.sex ? <span className="text-earth-500 font-normal"> · {l.animal.sex}</span> : null}</div>
                  <div className="text-xs text-earth-500 font-mono truncate">{l.animal?.rfid_tag || l.eid || ''}</div>
                </div>
                <div className="text-right text-xs text-earth-600">
                  <div>{l.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                  <div>{l.created ? (l.rfid_source === 'placeholder' ? 'registered · no LF tag' : 'registered') : l.already ? 'already bound' : 'bound to existing'}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// The Tagging day tab: the census switch, then one of the two forms.
function TaggingDay({ ranchId, canWrite, census, onCensusChange, profile, profileLoading, onBound, onRegistered, onNewAnimal }) {
  const baseline = profile?.herd_baseline_date || null
  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="census-toggle-row">
        <div className="min-w-0">
          <div className="font-medium text-primary-900 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Census mode</div>
          <p className="text-xs text-earth-600">
            For a herd that is not in the register yet: register each animal at the race as it is tagged.
            {baseline ? ` Animals are dated on the farm since ${baseline}.` : ''}
          </p>
        </div>
        <button type="button" role="switch" aria-checked={census} onClick={() => onCensusChange(!census)} data-testid="census-switch"
                className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors ${census ? 'bg-primary-600' : 'bg-earth-300'}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${census ? 'translate-x-8' : 'translate-x-1'}`} />
          <span className="sr-only">Census mode</span>
        </button>
      </div>
      {census && !profileLoading && !baseline && (
        <div className="card p-4 border-amber-200 bg-amber-50 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3" data-testid="baseline-banner">
          <span>Census mode dates every animal from the herd baseline date (census day 0), and this ranch has none yet.</span>
          <Link to="/ranch/settings" className="btn-secondary"><Settings className="h-4 w-4 mr-2" /> Set it in Ranch settings</Link>
        </div>
      )}
      {census
        ? <CensusForm key={ranchId} ranchId={ranchId} canWrite={canWrite} baseline={baseline} onRegistered={onRegistered} onBound={onBound} />
        : <CrushEntryForm ranchId={ranchId} canWrite={canWrite} onBound={onBound} onNewAnimal={onNewAnimal} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Profiles (Phase 4b-2): fill the blanks on crush-registered animals, in bulk
// ---------------------------------------------------------------------------
const MISSING_CHIPS = [['any', 'Any blank'], ['breed', 'No breed'], ['owner', 'No owner'], ['birth', 'No birth year'], ['markings', 'No markings']]
const MISSING_LABEL = { breed: 'breed', owner: 'owner', birth: 'birth year', markings: 'markings', name: 'name' }

function ProfilesTab({ ranchId, canWrite, onSaved, onAssignEid }) {
  const queryClient = useQueryClient()
  const [missing, setMissing] = useState('any')
  const [q, setQ] = useState('')
  const dq = useDebounced(q.trim(), 350)
  const [offset, setOffset] = useState(0)
  const [edits, setEdits] = useState({})       // animal id -> { breed_id, owner_segment_id, owner_segment, birth_year, color_markings, local_name }
  const [results, setResults] = useState({})   // animal id -> the row's result from the last save
  useEffect(() => { setOffset(0) }, [dq, missing])
  const list = useQuery({
    queryKey: ['smartkraalIncompleteProfiles', ranchId, missing, dq, offset],
    queryFn: ({ signal }) => getSmartKraalIncompleteProfiles(ranchId, { missing, q: dq || undefined, limit: PROFILE_PAGE, offset }, { signal }),
    enabled: !!ranchId, placeholderData: keepPreviousData,
  })
  const { data: breeds } = useQuery({ queryKey: ['breeds'], queryFn: getBreeds })
  const { data: owners } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }), enabled: !!ranchId,
  })
  const setEdit = (id, k, v) => setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }))
  // an invalid year (outside 1980..this year) is left out of the save and shown red in
  // its cell, so one slip never 422s the whole batch
  const rowBody = (id) => {
    const e = edits[id] || {}
    const other = e.owner_segment_id === '__other__'
    return {
      id,
      breed_id: e.breed_id || undefined,
      owner_segment_id: !other && e.owner_segment_id ? e.owner_segment_id : undefined,
      owner_segment: other && e.owner_segment?.trim() ? e.owner_segment.trim() : undefined,
      birth_year: e.birth_year && yearValid(e.birth_year) ? parseInt(e.birth_year, 10) : undefined,
      color_markings: e.color_markings?.trim() || undefined,
      local_name: e.local_name?.trim() || undefined,
    }
  }
  const dirty = Object.keys(edits).filter(id => Object.entries(rowBody(id)).some(([k, v]) => k !== 'id' && v !== undefined))
  const save = useMutation({
    mutationFn: () => completeSmartKraalProfiles(ranchId, { rows: dirty.map(rowBody) }),
    onSuccess: (out) => {
      const map = {}
      out.results.forEach(r => { map[r.id] = r })
      setResults(map)
      setEdits(e => Object.fromEntries(Object.entries(e).filter(([id]) => !map[id]?.ok)))
      queryClient.invalidateQueries({ queryKey: ['smartkraalIncompleteProfiles'] })
      queryClient.invalidateQueries({ queryKey: ['smartkraalRegCounts'] })
      queryClient.invalidateQueries({ queryKey: ['cattle'] })
      onSaved(out)
    },
    onError: (err) => onSaved({ error: errDetail(err) }),
  })
  const items = list.data?.items || []
  const total = list.data?.total || 0
  const resultText = (r) => {
    if (!r) return null
    if (!r.ok) return <span className="text-red-700">{{ not_crush_registered: 'not registered from the crush', birth_in_future: 'birth year is in the future', breed_unknown: 'unknown breed', owner_unknown: 'unknown owner', not_found: 'not found' }[r.error] || r.error}</span>
    const parts = []
    if (r.applied?.length) parts.push(`saved ${r.applied.map(k => MISSING_LABEL[k] || k).join(', ')}`)
    if (r.skipped_fields?.length) parts.push(`kept ${r.skipped_fields.map(k => MISSING_LABEL[k] || k).join(', ')} (already filled)`)
    return <span className={r.applied?.length ? 'text-primary-700' : 'text-earth-500'}>{parts.join('; ') || 'nothing to change'}</span>
  }
  return (
    <div className="space-y-4" data-testid="profiles-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-1" data-testid="profiles-chips">
          {MISSING_CHIPS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMissing(k)} aria-pressed={missing === k}
                    className={`rounded-full px-3 py-1 text-xs font-medium border ${missing === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-earth-700 border-earth-200 hover:bg-earth-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-3 text-earth-400" />
          <input value={q} onChange={e => setQ(e.target.value)} className="input pl-9" placeholder="Tag number, name, EID…" data-testid="profiles-search" />
        </div>
        <div className="text-sm text-earth-500">{list.isFetching ? 'Loading…' : `${total} animal${total === 1 ? '' : 's'} with a blank`}{total > PROFILE_PAGE && ` · showing ${offset + 1}–${Math.min(offset + PROFILE_PAGE, total)}`}</div>
        {total > PROFILE_PAGE && (
          <div className="flex gap-1">
            <button type="button" className="btn-secondary px-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PROFILE_PAGE))}><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" className="btn-secondary px-2" disabled={offset + PROFILE_PAGE >= total} onClick={() => setOffset(offset + PROFILE_PAGE)}><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
        {canWrite && (
          <button type="button" className="btn-primary sm:ml-auto" disabled={dirty.length === 0 || save.isPending} onClick={() => save.mutate()} data-testid="profiles-save">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save {dirty.length || ''}
          </button>
        )}
      </div>
      <p className="text-xs text-earth-500">
        Blank cells are editable; a filled cell is read-only here (correct it on the animal's page). Saving fills blanks only and never overwrites.
      </p>
      {list.error ? (
        <DataLoadError error={list.error} title="Could not load the profiles" onRetry={list.refetch} />
      ) : items.length === 0 && !list.isFetching ? (
        <div className="card p-10 text-center text-earth-500">
          <CheckCircle2 className="h-10 w-10 mx-auto text-primary-500 mb-2" />
          {dq ? `No crush-registered animal with a blank matches “${dq}”.` : missing === 'any' ? 'Every animal registered at the crush has a full profile.' : `No crush-registered animal is missing its ${MISSING_LABEL[missing]}.`}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-earth-500 border-b border-earth-200">
                <th className="py-2 px-3">Tag</th><th className="py-2 px-3">Animal</th><th className="py-2 px-3">Sex · class</th>
                <th className="py-2 px-3">Breed</th><th className="py-2 px-3">Owner</th><th className="py-2 px-3">Born</th>
                <th className="py-2 px-3">Markings</th><th className="py-2 px-3 text-right">Kraal</th><th className="py-2 px-3">Result</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => {
                const e = edits[a.id] || {}
                const miss = new Set(a.missing)
                const form = a.rfid_form || eidForm(a.rfid_tag)
                const editable = canWrite
                return (
                  <tr key={a.id} className="border-b border-earth-100 hover:bg-earth-50 align-top" data-testid={`profile-row-${a.id}`}>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-primary-900 text-lg leading-tight">{a.p_idtag ?? a.tag_id ?? '—'}</div>
                      {a.census && <div className="text-[10px] uppercase tracking-wide text-earth-500">census</div>}
                    </td>
                    <td className="py-2 px-3 min-w-[12rem]">
                      {a.local_name ? (
                        <Link to={`/cattle/${a.id}`} className="font-medium text-primary-700 hover:underline inline-flex items-center gap-1">{a.local_name} <ExternalLink className="h-3 w-3" /></Link>
                      ) : editable ? (
                        <input className="input py-1 text-sm" placeholder="Name (optional)" value={e.local_name || ''} onChange={ev => setEdit(a.id, 'local_name', ev.target.value)} data-testid={`profile-name-${a.id}`} />
                      ) : <Link to={`/cattle/${a.id}`} className="text-earth-500 hover:underline">unnamed</Link>}
                      <div className="text-xs font-mono text-earth-500 mt-0.5 flex flex-wrap items-center gap-2">
                        <span>{a.rfid_tag}</span>
                        {form !== 'eid' && (
                          <>
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-sans text-amber-800">{EID_FORM_LABEL[form]}</span>
                            {editable && <button type="button" className="font-sans text-primary-700 hover:underline" onClick={() => onAssignEid(a)} data-testid={`assign-eid-${a.id}`}>Assign EID</button>}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">{a.sex}{a.category ? ` · ${a.category}` : ''}</td>
                    <td className="py-2 px-3 min-w-[9rem]">
                      {miss.has('breed') && editable ? (
                        <select className="input py-1 text-sm" value={e.breed_id || ''} onChange={ev => setEdit(a.id, 'breed_id', ev.target.value)} data-testid={`profile-breed-${a.id}`}>
                          <option value="">—</option>
                          {(breeds || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      ) : <span className={miss.has('breed') ? 'text-earth-400' : ''}>{a.breed_name || '—'}</span>}
                    </td>
                    <td className="py-2 px-3 min-w-[10rem]">
                      {miss.has('owner') && editable ? (
                        <>
                          <select className="input py-1 text-sm" value={e.owner_segment_id || ''} onChange={ev => setEdit(a.id, 'owner_segment_id', ev.target.value)} data-testid={`profile-owner-${a.id}`}>
                            <option value="">—</option>
                            {(owners || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            <option value="__other__">Other (add new)…</option>
                          </select>
                          {e.owner_segment_id === '__other__' && (
                            <input className="input py-1 text-sm mt-1" placeholder="New owner name" value={e.owner_segment || ''} onChange={ev => setEdit(a.id, 'owner_segment', ev.target.value)} />
                          )}
                        </>
                      ) : <span className={miss.has('owner') ? 'text-earth-400' : ''}>{a.owner_name || a.owner_segment || '—'}</span>}
                    </td>
                    <td className="py-2 px-3">
                      {miss.has('birth') && editable ? (
                        <input className={`input py-1 text-sm w-24 ${e.birth_year && !yearValid(e.birth_year) ? 'border-red-400' : ''}`}
                               inputMode="numeric" maxLength={4} placeholder="year" value={e.birth_year || ''}
                               title={e.birth_year && !yearValid(e.birth_year) ? `A year between 1980 and ${THIS_YEAR}; this cell is not saved until it is.` : undefined}
                               onChange={ev => setEdit(a.id, 'birth_year', ev.target.value.replace(/\D/g, ''))} data-testid={`profile-year-${a.id}`} />
                      ) : <span className="whitespace-nowrap">{a.birth_date ? `${a.birth_date}${a.dob_precision && a.dob_precision !== 'day' ? ` (${a.dob_precision})` : ''}` : '—'}</span>}
                    </td>
                    <td className="py-2 px-3 min-w-[10rem]">
                      {miss.has('markings') && editable ? (
                        <input className="input py-1 text-sm" placeholder="e.g. red, white face" value={e.color_markings || ''} onChange={ev => setEdit(a.id, 'color_markings', ev.target.value)} data-testid={`profile-markings-${a.id}`} />
                      ) : <span>{a.color_markings || '—'}</span>}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap text-xs text-earth-600">
                      {a.n_weighings ? <>{kg(a.kraal_last_weight_kg)}<div>{a.n_weighings} pass{a.n_weighings === 1 ? '' : 'es'}</div></> : 'not weighed yet'}
                    </td>
                    <td className="py-2 px-3 text-xs" data-testid={`profile-result-${a.id}`}>{resultText(results[a.id])}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Plausibility checks (Phase 3) — stored on the link by the backend
// ---------------------------------------------------------------------------
const failingLabels = (checks) => (checks?.effective_failing || []).map(r => CHECK_RULE_LABEL[r] || r)

function ChecksFlag({ checks }) {
  if (!checks) return <span className="text-earth-400" title="Not checked yet; the checks run with the nightly sync"><Clock className="h-4 w-4" /></span>
  if (checks.result === 'fail') {
    return <span className="text-red-600 inline-flex items-center gap-1" title={failingLabels(checks).join(', ')}><Flag className="h-4 w-4" />{checks.effective_failing.length}</span>
  }
  if (checks.result === 'pass') return <span className="text-primary-600" title="Checks pass"><ShieldCheck className="h-4 w-4" /></span>
  return <span className="text-earth-400" title="No weights to check yet"><MinusCircle className="h-4 w-4" /></span>
}

function ChecksPanel({ checks, compact = false }) {
  if (!checks) {
    return (
      <p className="text-xs text-earth-500 flex items-center gap-1" data-testid="checks-panel">
        <Clock className="h-3.5 w-3.5" /> Not checked yet; the checks run with the nightly sync.
      </p>
    )
  }
  const res = CHECK_RESULT[checks.result] || CHECK_RESULT.insufficient
  const ack = new Set(checks.acknowledged?.rules || [])
  const rules = (checks.rules || []).filter(r => !compact || r.status !== 'skip')
  return (
    <div className="text-sm" data-testid="checks-panel">
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border ${res.cls}`}>
          {checks.result === 'fail' ? <Flag className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />} {res.label}
        </span>
        <span className="text-xs text-earth-500">{checks.n_readings} reading{checks.n_readings === 1 ? '' : 's'} · checked {shortEAT(checks.at)}</span>
      </div>
      {rules.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {rules.map(r => {
            const acked = r.status === 'fail' && ack.has(r.rule)
            const Icon = r.status === 'fail' ? (acked ? CheckCircle2 : XCircle) : r.status === 'pass' ? CheckCircle2 : MinusCircle
            const cls = r.status === 'fail' ? (acked ? 'text-amber-600' : 'text-red-600') : r.status === 'pass' ? 'text-primary-600' : 'text-earth-400'
            return (
              <li key={r.rule} className="flex gap-2">
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cls}`} />
                <div className="min-w-0">
                  <span className="font-medium text-gray-800">{CHECK_RULE_LABEL[r.rule] || r.rule}</span>
                  {acked && <span className="ml-1 text-xs text-amber-700">accepted by the reviewer</span>}
                  <div className={`text-xs ${r.status === 'skip' ? 'text-earth-500' : 'text-earth-700'}`}>{r.detail}</div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const ageText = (dob) => {
  if (!dob) return null
  const months = Math.floor((Date.now() - new Date(dob).getTime()) / (30.4375 * 86400000))
  if (months < 0) return null
  return months < 24 ? `${months} mo` : `${Math.floor(months / 12)} yr`
}

function Profile({ a }) {
  if (!a) return <div className="text-sm text-earth-500">Animal not found</div>
  const age = ageText(a.birth_date)
  return (
    <div className="text-sm">
      <Link to={`/cattle/${a.id}`} className="font-semibold text-primary-800 hover:underline inline-flex items-center gap-1">
        {animalName(a)} <ExternalLink className="h-3 w-3" />
      </Link>
      <div className="text-xs text-earth-600">
        {[a.sex, a.category, a.breed_name, a.birth_date ? `born ${a.birth_date}${age ? ` (${age})` : ''}` : 'no birth date', a.owner_segment]
          .filter(Boolean).join(' · ')}
      </div>
      <div className="text-xs text-earth-500 font-mono">{a.rfid_tag}{a.tag_id ? ` · tag ${a.tag_id}` : ''}</div>
    </div>
  )
}

function RecentWeights({ card }) {
  const recent = card.link?.checks?.recent
  if (recent?.length) {
    return (
      <div>
        <div className="text-xs uppercase tracking-wide text-earth-500 mb-1">Last weights</div>
        <div className="flex gap-4">
          {recent.map((w, i) => (
            <div key={i}>
              <div className="font-semibold text-gray-900">{kg(w.weight_kg)}</div>
              <div className="text-xs text-earth-500">{shortEAT(w.ts_utc)}{w.confidence != null ? ` · conf ${w.confidence}` : ''}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }
  return (
    <div className="text-sm text-earth-600">
      Last weight <span className="font-medium text-gray-900">{kg(card.last_weight_kg)}</span>
      {card.last_confidence != null ? ` (conf ${card.last_confidence})` : ''} · {card.n_weighings} pass{card.n_weighings === 1 ? '' : 'es'}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Review card (Phase 3): the reviewer's Awaiting-review view and everyone's
// Sent-back view
// ---------------------------------------------------------------------------
function ReviewCard({ card, canReview, canWrite, busy, onConfirm, onSendBack, onResubmit, onRebind, onRetire }) {
  const link = card.link
  if (!link) {
    // Only a drawn audit sample can carry such a card (the tag is pinned by id and its
    // binding was retired after the draw): say so, so the sample count still adds up.
    return (
      <div className="card p-4 flex flex-col gap-2 border-dashed" data-testid={`review-card-${card.p_idtag}`}>
        <div className="text-xs uppercase tracking-wide text-earth-500">SmartKraal tag</div>
        <div className="text-3xl font-bold text-primary-900 leading-tight">{card.p_idtag}</div>
        <p className="text-sm text-earth-600">Its binding was withdrawn after this list was drawn; nothing to review.</p>
      </div>
    )
  }
  const sentBack = card.status === 'sent_back'
  const confirmed = card.status === 'confirmed'
  const hasAnimal = !!card.animal
  const border = sentBack ? 'border-red-200' : link.flagged ? 'border-amber-300' : ''
  return (
    <div className={`card p-4 flex flex-col gap-3 ${border}`} data-testid={`review-card-${card.p_idtag}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-earth-500">SmartKraal tag</div>
          <div className="text-3xl font-bold text-primary-900 leading-tight">{card.p_idtag}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusChip status={card.status} />
          {!card.seen_by_kraal && <span className="rounded-full bg-earth-100 px-2 py-0.5 text-xs text-earth-600">not read yet</span>}
        </div>
      </div>
      <Stills media={card.media} weighed={card.n_weighings > 0} />
      <Profile a={card.animal} />
      <RecentWeights card={card} />
      <div className="text-xs text-earth-500">
        Bound {shortEAT(link.bound_at)}{link.binder_name ? ` by ${link.binder_name}` : ''} · {METHOD[link.bind_method] || link.bind_method}
      </div>
      <ChecksPanel checks={link.checks} compact />
      {link.review_note && (
        <div className={`rounded-lg p-3 text-sm ${sentBack ? 'bg-red-50 border border-red-100' : 'bg-earth-50 border border-earth-100'}`} data-testid={`review-note-${card.p_idtag}`}>
          <div className="text-xs uppercase tracking-wide text-earth-500">
            {sentBack ? 'Sent back' : 'Reviewer'}{link.reviewer_name ? ` by ${link.reviewer_name}` : ''}{link.reviewed_at ? ` · ${shortEAT(link.reviewed_at)}` : ''}
          </div>
          <div className="mt-0.5 text-gray-800">{link.review_note}</div>
        </div>
      )}
      {canReview && (
        <div className="flex gap-2 mt-auto">
          {/* a confirmed binding offers Confirm again only when a rule started failing after the review */}
          {(!confirmed || link.flagged) && (
            <button type="button" className="btn-primary flex-1" disabled={busy || !hasAnimal} onClick={() => onConfirm(card)} data-testid={`confirm-link-${card.p_idtag}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              {sentBack ? 'Confirm after all' : confirmed ? 'Accept the new flags' : 'Confirm'}
            </button>
          )}
          {!sentBack && (
            <button type="button" className="btn-secondary flex-1" disabled={busy} onClick={() => onSendBack(card)} data-testid={`send-back-${card.p_idtag}`}>
              <Send className="h-4 w-4 mr-2" /> Send back
            </button>
          )}
        </div>
      )}
      {canWrite && sentBack && (
        <div className="mt-auto space-y-2">
          {!hasAnimal && <p className="text-xs text-red-700">The animal was removed from the register; retire this binding.</p>}
          <div className="flex gap-2">
            <button type="button" className="btn-primary flex-1" disabled={busy || !hasAnimal} onClick={() => onResubmit(card)} data-testid={`resubmit-${card.p_idtag}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />} Resubmit
            </button>
            <button type="button" className="btn-secondary flex-1" disabled={busy} onClick={() => onRebind(card)} data-testid={`rebind-${card.p_idtag}`}>
              <Search className="h-4 w-4 mr-2" /> Re-bind…
            </button>
          </div>
          <div className="flex justify-between text-xs">
            {card.animal && (
              <Link to={`/cattle/${card.animal.id}/edit`} className="text-primary-700 hover:underline inline-flex items-center gap-1">
                <Pencil className="h-3 w-3" /> Edit profile
              </Link>
            )}
            <button type="button" className="text-red-700 hover:underline" onClick={() => onRetire(card)}>Retire…</button>
          </div>
        </div>
      )}
      {canWrite && !canReview && !sentBack && (
        <div className="flex justify-end text-xs"><button type="button" className="text-red-700 hover:underline" onClick={() => onRetire(card)}>Retire…</button></div>
      )}
    </div>
  )
}

function SendBackForm({ row, busy, error, onClose, onSubmit }) {
  const [note, setNote] = useState('')
  return (
    <Modal title={`Send back the binding of tag ${row.p_idtag}`} subtitle={`${animalName(row.animal)} · ${row.animal?.rfid_tag || ''}`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); onSubmit({ note: note.trim() }) }} className="space-y-3 text-sm">
        <p className="text-earth-700">
          The farm sees your note on its Sent-back list. The tag's SmartKraal weights come off {animalName(row.animal)} until the farm
          resubmits the pair, binds the tag to another animal, or you confirm after all.
        </p>
        <div>
          <label className="label" htmlFor="send-back-note">What made you doubt it? *</label>
          <textarea id="send-back-note" className="input" rows={3} required minLength={3} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="e.g. the photo shows a steer; the register says cow" data-testid="send-back-note" autoFocus />
        </div>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-danger" disabled={busy || note.trim().length < 3} data-testid="send-back-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send back
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Tables for bound / sent back / all
// ---------------------------------------------------------------------------
function LinksTable({ items, canWrite, onRetire, onFind, onNew, loading = false }) {
  if (items.length === 0) return <p className="text-sm text-earth-500 p-6 text-center">{loading ? 'Loading…' : 'Nothing here.'}</p>
  return (
    <div className="card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-earth-500 border-b border-earth-200">
            <th className="py-2 px-3">Tag</th><th className="py-2 px-3">Animal</th><th className="py-2 px-3">EID</th>
            <th className="py-2 px-3">Status</th><th className="py-2 px-3">Checks</th><th className="py-2 px-3">Bound</th>
            <th className="py-2 px-3 text-right">Last weight</th><th className="py-2 px-3 text-right">Passes</th>
            <th className="py-2 px-3">Last seen</th><th className="py-2 px-3" />
          </tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.p_idtag} className="border-b border-earth-100 hover:bg-earth-50" data-testid={`tag-row-${c.p_idtag}`}>
              <td className="py-2 px-3 font-semibold text-primary-900">{c.p_idtag}{!c.seen_by_kraal && <span className="ml-1 text-xs font-normal text-earth-500">(not read yet)</span>}</td>
              <td className="py-2 px-3">
                {c.animal ? (
                  <Link to={`/cattle/${c.animal.id}`} className="text-primary-700 hover:underline inline-flex items-center gap-1">
                    {animalName(c.animal)} <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : c.proposal ? <span className="text-earth-500">looks like {animalName(c.proposal.animal)}</span> : <span className="text-earth-400">—</span>}
              </td>
              <td className="py-2 px-3 font-mono text-xs">{c.animal?.rfid_tag || ''}</td>
              <td className="py-2 px-3"><StatusChip status={c.status} />
                {c.link?.bind_method && <div className="text-xs text-earth-500">{METHOD[c.link.bind_method] || c.link.bind_method}</div>}
                {c.status === 'sent_back' && c.link?.review_note && <div className="text-xs text-red-700 max-w-xs truncate" title={c.link.review_note}>“{c.link.review_note}”</div>}
              </td>
              <td className="py-2 px-3" data-testid={`checks-${c.p_idtag}`}>{c.link ? <ChecksFlag checks={c.link.checks} /> : ''}</td>
              <td className="py-2 px-3 whitespace-nowrap text-xs">{c.link?.bound_at ? shortEAT(c.link.bound_at) : ''}</td>
              <td className="py-2 px-3 text-right">{kg(c.last_weight_kg)}</td>
              <td className="py-2 px-3 text-right">{c.n_weighings}</td>
              <td className="py-2 px-3 whitespace-nowrap text-xs">{shortEAT(c.last_seen_utc)}</td>
              <td className="py-2 px-3 text-right whitespace-nowrap">
                {canWrite && c.link && (
                  <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => onRetire(c)}>Retire…</button>
                )}
                {canWrite && !c.link && (
                  <>
                    <button type="button" className="text-xs text-primary-700 hover:underline mr-3" onClick={() => onFind(c)}>Find animal</button>
                    <button type="button" className="text-xs text-primary-700 hover:underline" onClick={() => onNew(c)}>New animal</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Onboarding: the day-90 acceptance summary (Phase 4c)
// ---------------------------------------------------------------------------
// Advisory. The regional manager and the DAO accept the farm's register against
// this page; "Copy as text" gives them the markdown for the acceptance record.
// Everything is as of a date (the binding history decides each binding's state
// at the end of that Kampala day), so the summary can be read for day 90 exactly.
const pctText = (v) => (v == null ? '—' : `${v}%`)
const DAY0_SOURCE = {
  given: 'set here', herd_baseline_date: 'the herd baseline date in Ranch settings',
  ranch_created: 'the day the ranch was created in WildeBeef',
  ranch_created_baseline_future: 'the day the ranch was created in WildeBeef; the herd baseline date in Ranch settings is still in the future',
  none: 'unknown',
}
const ROLE_LABEL = {
  manager: 'Manager', herdsman: 'Herder', owner: 'Owner', viewer: 'Viewer', assurance_officer: 'Assurance officer',
  system_admin: 'System admin', admin: 'System admin', external_auditor: 'External auditor',
}
const OP_WORD = { '>=': 'at least', '<=': 'at most', '==': 'exactly' }
const gateValueText = (g) => (g.value == null ? 'not measurable' : g.unit === '%' ? `${g.value}%` : `${g.value} ${g.unit}`)
const gateThresholdText = (g) => `${OP_WORD[g.op] || g.op} ${g.threshold}${g.unit === '%' ? '%' : ` ${g.unit}`}`
const failing = (d) => (d?.gates || []).filter(g => !g.pass).length

function acceptanceMarkdown(d) {
  const n = (v) => (v == null ? '-' : v)
  const L = []
  L.push(`# Registration acceptance summary - as of ${d.as_of}`)
  L.push('')
  L.push(`Day ${d.day} since day 0 (${d.day0}, ${DAY0_SOURCE[d.day0_source] || d.day0_source}). ` +
    `Kraal first read: ${d.kraal?.first_read_utc ? `${shortEAT(d.kraal.first_read_utc)} (day ${d.kraal.first_read_day})` : 'never'}. ` +
    `Data set as of ${d.data_as_of_utc ? formatEAT(d.data_as_of_utc) : 'unknown'}.`)
  L.push('', `## Gates: ${d.all_pass ? 'all pass' : `${failing(d)} of ${d.gates.length} fail`}`, '')
  L.push('| Gate | Value | Threshold | Result |', '|---|---|---|---|')
  d.gates.forEach(g => L.push(`| ${g.label} | ${gateValueText(g)} | ${gateThresholdText(g)} | ${g.pass ? 'PASS' : 'FAIL'} |`))
  const b = d.bindings, c = b.coverage, h = d.herd
  L.push('', '## Herd and bindings', '')
  L.push(`- Active animals: ${h.active} (census ${h.by_source.census}, crush ${h.by_source.crush}, other ${h.by_source.other})`)
  L.push(`- Coverage: ${c.covered} of ${c.active} wear a bound or confirmed tag (${pctText(c.pct)}); confirmed ${c.confirmed_animals}, awaiting review ${c.awaiting_animals}, sent back ${c.sent_back_animals}`)
  L.push(`- Live bindings: ${b.live} (awaiting review ${b.bound}, confirmed ${b.confirmed}, sent back ${b.sent_back})`)
  L.push(`- Reviewed: ${b.reviewed} of ${b.live} (${pctText(b.reviewed_pct)}); last word confirmed ${b.confirmed_of_reviewed} (${pctText(b.confirmed_of_reviewed_pct)}), sent back ${b.sent_back_of_reviewed} (${pctText(b.sent_back_of_reviewed_pct)})`)
  L.push(`- Sent back and open: ${b.sent_back_open}${b.sent_back_open_median_days != null ? ` (median ${b.sent_back_open_median_days} days open)` : ''}; flagged by the checks now: ${b.flagged_open}`)
  const f = d.flows
  L.push(`- Since day 0: ${f.bound} bound, of which ${f.resubmitted} resubmitted after a send-back (new bindings: ${Object.entries(f.bound_by_method || {}).map(([k, v]) => `${METHOD[k] || k} ${v}`).join(', ') || 'none'}); ${f.confirmed} confirmed, ${f.sent_back} sent back, ${f.retired} retired (${f.retired_wrong_animal} wrong animal)`)
  L.push('', '## Tags and weights', '')
  L.push(`- Tags the kraal knows: ${d.tags.seen}; weighed: ${d.tags.weighed}; weighed tags no animal wears: ${d.tags.unbound_with_weights}`)
  L.push(`- Animals with a SmartKraal weight: ${d.weights.animals_1} (${pctText(d.weights.pct_1)}); with two or more: ${d.weights.animals_2} (${pctText(d.weights.pct_2)})`)
  const p = d.profiles
  L.push('', '## Profiles', '')
  L.push(`- Complete (breed, owner, birth estimate): ${p.complete} (${pctText(p.pct.complete)}); breed ${pctText(p.pct.breed)}, owner ${pctText(p.pct.owner)}, birth ${pctText(p.pct.birth)}, photo ${pctText(p.pct.photo)}, markings ${pctText(p.pct.markings)}`)
  L.push('', '## Timeline (Kampala weeks)', '')
  L.push('| Week | Bound | Confirmed | Sent back | Retired | Coverage |', '|---|---|---|---|---|---|')
  d.timeline.weeks.forEach(w => L.push(`| ${w.week_start} to ${w.week_end} | ${w.bound} | ${w.confirmed} | ${w.sent_back} | ${w.retired} | ${w.covered}/${w.herd} (${pctText(w.coverage_pct)}) |`))
  L.push('', `Coverage reached 50% by the week ending ${n(d.timeline.coverage_50_week_end)}; 90% by ${n(d.timeline.coverage_90_week_end)}.`)
  L.push('', '## People', '')
  L.push('| Name | Role | Bound | Resubmitted | Confirmed | Sent back | Retired |', '|---|---|---|---|---|---|---|')
  d.people.forEach(u => L.push(`| ${u.name} | ${ROLE_LABEL[u.role] || u.role || '-'} | ${u.bound} | ${u.resubmitted} | ${u.confirmed} | ${u.sent_back} | ${u.retired} |`))
  const t = d.context.trust, s = d.context.scorecard
  L.push('', '## Context', '')
  L.push(`- Trust score: ${t ? `${t.score} (${t.band}, ${t.on_track ? 'on track' : 'behind'} for day ${t.days_since_onboarding}, as of ${t.as_of})` : 'none yet'}`)
  L.push(`- Weekly scorecard: ${s.weeks_passed} of ${s.weeks_total} frozen weeks passed`)
  L.push(`- Open SmartKraal exceptions: ${d.context.open_exceptions.length ? d.context.open_exceptions.map(e => `${e.type} (${e.count})`).join(', ') : 'none'}`)
  L.push('', `_${d.note}_`, '')
  L.push('Accepted by (regional manager): ______________________   Date: ____________')
  L.push('', 'Accepted by (Data Assurance Officer): _______________   Date: ____________')
  return L.join('\n')
}

function GatePill({ g }) {
  const cls = !g.measured ? 'bg-earth-100 text-earth-600' : g.pass ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{!g.measured ? 'not measurable' : g.pass ? 'pass' : 'fail'}</span>
}

function OnboardingTab({ ranchId }) {
  const [asOf, setAsOf] = useState(kampalaToday())
  const [day0, setDay0] = useState('')
  const [copied, setCopied] = useState(null)   // { text, toClipboard } after Copy as text
  const q = useQuery({
    queryKey: ['smartkraalAcceptance', ranchId, asOf, day0],
    queryFn: ({ signal }) => getSmartKraalRegistrationAcceptance(ranchId, { as_of: asOf || undefined, day0: day0 || undefined }, { signal }),
    enabled: !!ranchId, placeholderData: keepPreviousData, retry: false,
  })
  const d = q.data
  const rangeError = q.error && errCode(q.error) === 'as_of_out_of_range' ? errDetail(q.error) : null
  const copy = async () => {
    const text = acceptanceMarkdown(d)
    let ok = false
    try { await navigator.clipboard.writeText(text); ok = true } catch { ok = false }
    setCopied({ text, toClipboard: ok })
  }
  const nFail = failing(d)
  return (
    <div className="space-y-4" data-testid="onboarding-tab">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-earth-500 mb-1">As of</span>
          <input type="date" value={asOf} max={kampalaToday()} onChange={e => setAsOf(e.target.value)} className="input" data-testid="acceptance-as-of" />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-earth-500 mb-1">Day 0 (blank = automatic)</span>
          <input type="date" value={day0} onChange={e => setDay0(e.target.value)} className="input" data-testid="acceptance-day0" />
        </label>
        <div className="flex-1 text-sm text-earth-500">
          {q.isFetching ? 'Loading…' : d?.configured === false ? '' : d ? `Day ${d.day} since ${d.day0} (${DAY0_SOURCE[d.day0_source] || d.day0_source}) · kraal first read ${d.kraal?.first_read_utc ? `${shortEAT(d.kraal.first_read_utc)}, day ${d.kraal.first_read_day}` : 'never'} · data set as of ${formatEAT(d.data_as_of_utc)}` : ''}
        </div>
        <button type="button" onClick={copy} className="btn-secondary" disabled={!d || d.configured === false} data-testid="acceptance-copy">
          <Copy className="h-4 w-4 mr-2" /> Copy as text
        </button>
      </div>

      {rangeError ? (
        <div className="card p-4 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="acceptance-range-error">{rangeError}</div>
      ) : q.error ? (
        <DataLoadError error={q.error} title="Could not load the acceptance summary" onRetry={q.refetch} />
      ) : !d ? null : d.configured === false ? (
        <div className="card p-6 text-sm text-earth-600">SmartKraal is not configured for this ranch.</div>
      ) : (
        <>
          <div className={`card p-4 flex items-start gap-3 ${d.all_pass ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`} data-testid="all-pass">
            {d.all_pass ? <ClipboardCheck className="h-5 w-5 text-green-700 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />}
            <div className="text-sm">
              <div className="font-medium">{d.all_pass ? `All ${d.gates.length} gates pass as of ${d.as_of}.` : `${nFail} of ${d.gates.length} gates fail as of ${d.as_of}.`}</div>
              <div className="text-earth-600">Advisory: the regional manager and the Data Assurance Officer accept against this summary. Copy it as text into the acceptance record.</div>
              {d.history_consistent === false && <div className="text-red-700 mt-1">The binding history and the current bindings disagree; read today's numbers from the other tabs until the history is repaired.</div>}
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="min-w-full text-sm" data-testid="gates-table">
              <thead className="bg-earth-50 text-xs uppercase tracking-wider text-earth-500">
                <tr><th className="px-4 py-2 text-left">Gate</th><th className="px-4 py-2 text-right">Value</th><th className="px-4 py-2 text-left">Threshold</th><th className="px-4 py-2 text-left">Result</th></tr>
              </thead>
              <tbody className="divide-y divide-earth-100">
                {d.gates.map(g => (
                  <tr key={g.key} data-testid={`gate-${g.key}`} data-pass={g.pass ? 'true' : 'false'}>
                    <td className="px-4 py-2">{g.label}</td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums">{gateValueText(g)}</td>
                    <td className="px-4 py-2 text-earth-600">{gateThresholdText(g)}</td>
                    <td className="px-4 py-2"><GatePill g={g} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="card p-4 space-y-1" data-testid="acceptance-herd">
              <div className="text-xs uppercase tracking-wider text-earth-500">Herd as of {d.as_of}</div>
              <div className="text-2xl font-bold">{d.herd.active}</div>
              <div className="text-earth-600">census {d.herd.by_source.census} · crush {d.herd.by_source.crush} · other {d.herd.by_source.other}</div>
              <div className="text-earth-600">{d.bindings.coverage.covered} covered ({pctText(d.bindings.coverage.pct)})</div>
            </div>
            <div className="card p-4 space-y-1" data-testid="acceptance-bindings">
              <div className="text-xs uppercase tracking-wider text-earth-500">Bindings</div>
              <div className="text-2xl font-bold">{d.bindings.live}</div>
              <div className="text-earth-600">awaiting review {d.bindings.bound} · confirmed {d.bindings.confirmed} · sent back {d.bindings.sent_back}</div>
              <div className="text-earth-600">reviewed {d.bindings.reviewed} ({pctText(d.bindings.reviewed_pct)}): confirmed {d.bindings.confirmed_of_reviewed}, sent back {d.bindings.sent_back_of_reviewed}</div>
              <div className="text-earth-600">{d.bindings.sent_back_open} open send-back{d.bindings.sent_back_open === 1 ? '' : 's'}{d.bindings.sent_back_open_median_days != null ? ` (median ${d.bindings.sent_back_open_median_days} d)` : ''} · {d.bindings.flagged_open} flagged now</div>
            </div>
            <div className="card p-4 space-y-1" data-testid="acceptance-weights">
              <div className="text-xs uppercase tracking-wider text-earth-500">Weights and tags</div>
              <div className="text-2xl font-bold">{pctText(d.weights.pct_1)}</div>
              <div className="text-earth-600">{d.weights.animals_1} animals with a SmartKraal weight · {d.weights.animals_2} with two or more ({pctText(d.weights.pct_2)})</div>
              <div className="text-earth-600">tags known {d.tags.seen} · weighed {d.tags.weighed} · <span className={d.tags.unbound_with_weights ? 'text-red-700' : ''}>{d.tags.unbound_with_weights} weighed without an animal</span></div>
            </div>
            <div className="card p-4 space-y-1" data-testid="acceptance-profiles">
              <div className="text-xs uppercase tracking-wider text-earth-500">Profiles</div>
              <div className="text-2xl font-bold">{pctText(d.profiles.pct.complete)}</div>
              <div className="text-earth-600">{d.profiles.complete} complete (breed, owner, birth estimate)</div>
              <div className="text-earth-600">breed {pctText(d.profiles.pct.breed)} · owner {pctText(d.profiles.pct.owner)} · birth {pctText(d.profiles.pct.birth)} · photo {pctText(d.profiles.pct.photo)} · markings {pctText(d.profiles.pct.markings)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card overflow-hidden">
              <div className="px-4 py-2 border-b border-earth-100 text-sm font-medium">Week by week since day 0
                <span className="text-earth-500 font-normal"> · 50% coverage {d.timeline.coverage_50_week_end ? `by ${d.timeline.coverage_50_week_end}` : 'not yet'} · 90% {d.timeline.coverage_90_week_end ? `by ${d.timeline.coverage_90_week_end}` : 'not yet'}</span>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm" data-testid="acceptance-weeks">
                  <thead className="bg-earth-50 text-xs uppercase tracking-wider text-earth-500 sticky top-0">
                    <tr><th className="px-3 py-2 text-left">Week</th><th className="px-3 py-2 text-right">Bound</th><th className="px-3 py-2 text-right">Confirmed</th><th className="px-3 py-2 text-right whitespace-nowrap">Sent back</th><th className="px-3 py-2 text-right whitespace-nowrap">Coverage</th></tr>
                  </thead>
                  <tbody className="divide-y divide-earth-100 tabular-nums">
                    {d.timeline.weeks.map(w => (
                      <tr key={w.week_start}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{w.week_start} → {w.week_end}</td>
                        <td className="px-3 py-1.5 text-right">{w.bound || <span className="text-earth-300">0</span>}</td>
                        <td className="px-3 py-1.5 text-right">{w.confirmed || <span className="text-earth-300">0</span>}</td>
                        <td className="px-3 py-1.5 text-right">{w.sent_back || <span className="text-earth-300">0</span>}</td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">{pctText(w.coverage_pct)} <span className="text-earth-500 text-xs">({w.covered}/{w.herd})</span></td>
                      </tr>
                    ))}
                    {d.timeline.weeks.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-earth-500">No week yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="space-y-4">
              <div className="card overflow-hidden">
                <div className="px-4 py-2 border-b border-earth-100 text-sm font-medium">Who did the work since day 0</div>
                <table className="min-w-full text-sm" data-testid="acceptance-people">
                  <thead className="bg-earth-50 text-xs uppercase tracking-wider text-earth-500">
                    <tr><th className="px-3 py-2 text-left">Person</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-right">Bound</th><th className="px-3 py-2 text-right">Confirmed</th><th className="px-3 py-2 text-right">Sent back</th></tr>
                  </thead>
                  <tbody className="divide-y divide-earth-100 tabular-nums">
                    {d.people.map(u => (
                      <tr key={u.user_id || u.name}>
                        <td className="px-3 py-1.5">{u.name}</td>
                        <td className="px-3 py-1.5 text-earth-600">{ROLE_LABEL[u.role] || u.role || '—'}</td>
                        <td className="px-3 py-1.5 text-right">{u.bound}{u.resubmitted ? <span className="text-earth-500"> (+{u.resubmitted} resubmitted)</span> : ''}</td>
                        <td className="px-3 py-1.5 text-right">{u.confirmed}</td>
                        <td className="px-3 py-1.5 text-right">{u.sent_back}</td>
                      </tr>
                    ))}
                    {d.people.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-earth-500">No binding yet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="card p-4 text-sm space-y-1" data-testid="acceptance-context">
                <div className="text-xs uppercase tracking-wider text-earth-500">Around the register</div>
                <div>Trust score: {d.context.trust ? <>{d.context.trust.score} <span className="text-earth-600">({d.context.trust.band}, {d.context.trust.on_track ? 'on track' : 'behind'} for day {d.context.trust.days_since_onboarding}, as of {d.context.trust.as_of})</span></> : <span className="text-earth-500">none yet</span>}</div>
                <div>Weekly scorecard: {d.context.scorecard.weeks_passed} of {d.context.scorecard.weeks_total} frozen weeks passed</div>
                <div>Open SmartKraal exceptions: {d.context.open_exceptions.length ? d.context.open_exceptions.map(e => `${e.type} (${e.count})`).join(', ') : 'none'}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {copied && (
        <Modal title="Acceptance summary as text" subtitle={copied.toClipboard ? 'Copied to the clipboard. Paste it into the acceptance record.' : 'Select the text and copy it into the acceptance record.'}
               onClose={() => setCopied(null)} wide>
          <textarea readOnly value={copied.text} className="input font-mono text-xs h-96 w-full" data-testid="acceptance-text" onFocus={e => e.target.select()} />
        </Modal>
      )}
    </div>
  )
}

export default function CrushRegistration({ ranchId }) {
  const { canWrite, role } = useAuth()
  const canReview = canReviewRegistration(role)
  // The farm answers a send-back (Resubmit / Re-bind / Retire); the assurance officer
  // is read-only by constitution and reviews only. The system admin may do both.
  const canAnswer = canWrite && role !== 'assurance_officer'
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const counts = useQuery({
    queryKey: ['smartkraalRegCounts', ranchId],
    queryFn: ({ signal }) => getSmartKraalRegistrationCounts(ranchId, { signal }),
    enabled: !!ranchId,
  })
  const tab = TABS.some(t => t.key === params.get('tab')) ? params.get('tab') : 'unbound'
  const setTab = (k) => { setParams(k === 'unbound' ? {} : { tab: k }); setOffset(0); setFlaggedOnly(false); setSample(null) }
  // A farm registering from zero (no active animal yet) opens on Tagging day, where
  // census mode lives. The choice is written to the URL once, so the page does not
  // jump to the queue the moment the first animal makes the herd non-empty.
  const emptyHerd = counts.data?.herd_active === 0
  useEffect(() => {
    if (emptyHerd && !params.get('tab')) setParams({ tab: 'tagging' }, { replace: true })
  }, [emptyHerd])
  // Census mode is a per-ranch preference on this device; a herd with no animal
  // yet starts with it on, and that start is remembered like a choice.
  const [census, setCensus] = useState(false)
  const setCensusMode = (on) => { setCensus(on); try { localStorage.setItem(CENSUS_KEY(ranchId), on ? '1' : '0') } catch { /* private mode */ } }
  useEffect(() => {
    if (counts.data === undefined) return
    let stored = null
    try { stored = localStorage.getItem(CENSUS_KEY(ranchId)) } catch { /* private mode */ }
    if (stored != null) setCensus(stored === '1')
    else if (emptyHerd) setCensusMode(true)
    else setCensus(false)
  }, [ranchId, emptyHerd, counts.data === undefined])
  const profile = useQuery({ queryKey: ['operatingProfile', ranchId], queryFn: () => getOperatingProfile(ranchId), enabled: !!ranchId })
  const [assigning, setAssigning] = useState(null)   // the animal whose placeholder electronic id is being replaced
  const [q, setQ] = useState('')
  const dq = useDebounced(q.trim(), 350)
  const [offset, setOffset] = useState(0)
  const [flaggedOnly, setFlaggedOnly] = useState(false)   // review tab: only bindings the checks flag
  // Review tab: a random sample is DRAWN once (its tag ids kept here) and then re-read
  // by id, so confirming a card does not draw a replacement; the set is the audit sample.
  const [sample, setSample] = useState(null)              // { tags: [p_idtag], drawn: N } | null
  const [drawing, setDrawing] = useState(false)
  const [flash, setFlash] = useState(null)      // {kind: 'ok'|'warn'|'error', text, link?}
  const [picker, setPicker] = useState(null)    // card (+ replaceSentBack when re-binding a sent-back tag)
  const [creating, setCreating] = useState(null) // {card?, pIdtag?, rfid?}
  const [retiring, setRetiring] = useState(null) // row
  const [sendingBack, setSendingBack] = useState(null) // row
  const [actionError, setActionError] = useState('')
  useEffect(() => { setOffset(0) }, [dq])

  const tabDef = TABS.find(t => t.key === tab)
  // One builder for the queue key (useQuery below and drawSample's cache seed), so the
  // slots the placeholder predicate reads cannot drift from the key.
  const queueKey = (status, tags, off = offset) => ['smartkraalRegQueue', ranchId, status, dq, off, flaggedOnly, tags]
  const queue = useQuery({
    queryKey: queueKey(tabDef?.status, sample?.tags),
    queryFn: ({ signal }) => getSmartKraalRegistrationQueue(ranchId, sample
      ? { status: 'all', tags: sample.tags.join(','), limit: 200, offset: 0 }   // the drawn set, whatever its status now
      : { status: tabDef.status, q: dq || undefined, limit: PAGE, offset, flagged: flaggedOnly || undefined },
    { signal }),
    enabled: !!ranchId && !!tabDef?.status,
    // Keep the previous page on screen while paging or searching WITHIN a tab (no flicker),
    // never across tabs, ranches or in/out of a drawn sample: another bucket's cards are
    // the wrong shape for this layout (unbound cards have no binding; the review card
    // reads it) and another ranch's cards would carry live action buttons.
    placeholderData: (prev, prevQuery) => {
      const [, prevRanch, prevStatus, , , , prevTags] = prevQuery?.queryKey || []
      return prevRanch === ranchId && prevStatus === tabDef?.status && prevTags === sample?.tags ? prev : undefined
    },
  })
  const drawSample = async (status, n) => {
    setDrawing(true)
    try {
      const drawn = await getSmartKraalRegistrationQueue(ranchId, { status, sample: n, limit: n })
      const tags = (drawn.items || []).map(i => i.p_idtag)
      // The drawn cards are the sample's first render: seed the query the page is
      // about to run (same key) so the draw costs one request, not two.
      queryClient.setQueryData(queueKey(tabDef?.status, tags, 0),
        { ...drawn, status: 'all', total: tags.length, limit: 200, offset: 0 })
      setSample({ tags, drawn: n })
      setOffset(0)
    } catch (err) {
      setFlash({ kind: 'error', text: errDetail(err) })
    } finally {
      setDrawing(false)
    }
  }

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['smartkraalRegQueue'] })
    queryClient.invalidateQueries({ queryKey: ['smartkraalRegCounts'] })
    queryClient.invalidateQueries({ queryKey: ['smartkraalIncompleteProfiles'] })
    queryClient.invalidateQueries({ queryKey: ['cattle'] })
    queryClient.invalidateQueries({ queryKey: ['smartkraalWeighings'] })
  }
  const promoNote = (out) => {
    if (out.promotion?.error) return ` The binding is saved but its weights could not be linked just now (${out.promotion.error}); the nightly sync retries.`
    const n = out.promotion?.promoted ?? 0
    return n ? ` ${n} weight${n === 1 ? '' : 's'} added to the animal.` : ''
  }
  const checksNote = (out) => {
    const f = failingLabels(out.link?.checks)
    return f.length ? ` The checks flag this binding (${f.join(', ')}); the reviewer will look at it.` : ''
  }
  const said = (out, verb) => {
    const w = out.warnings?.includes('tag_not_seen') ? ' The kraal has not read this tag yet; its weights link from the next dip day.' : ''
    const again = out.rebound ? ' (re-asserted after the reviewer sent it back; the reviewer sees it again)' : ''
    const replaced = out.warnings?.includes('sent_back_binding_retired') ? ' The sent-back binding was withdrawn as wrong.' : ''
    return `${verb} tag ${out.link.p_idtag} to ${animalName(out.animal)}${again}.${replaced}${promoNote(out)}${w}${checksNote(out)}`
  }

  const bindMut = useMutation({
    mutationFn: ({ body }) => bindSmartKraalTag(ranchId, body),
    onSuccess: (out) => {
      const flagged = failingLabels(out.link?.checks).length > 0
      setFlash({ kind: out.promotion?.error || flagged ? 'warn' : 'ok', text: said(out, 'Bound'), link: `/cattle/${out.animal.id}` })
      setPicker(null); setActionError(''); refresh()
    },
    onError: (err) => {
      const msg = errDetail(err)
      if (picker) setActionError(msg); else setFlash({ kind: 'error', text: msg })
    },
  })
  const confirmMut = useMutation({
    mutationFn: ({ linkId }) => confirmSmartKraalTagLink(ranchId, linkId, {}),
    onSuccess: (out) => {
      const back = out.was === 'sent_back' ? ` Its weights are back on the animal (${out.promotion?.promoted ?? 0}).` : ''
      const acked = out.link?.checks?.acknowledged?.rules?.length
      const err = out.checks?.error ? ` The checks could not be re-run just now (${out.checks.error}); the nightly sync will.` : ''
      setFlash({ kind: err ? 'warn' : 'ok', text: `Confirmed tag ${out.link.p_idtag} on ${animalName(out.animal)}.${back}${acked ? ` ${acked} failing check${acked === 1 ? '' : 's'} accepted.` : ''}${err}`, link: `/cattle/${out.animal.id}` })
      setActionError(''); refresh()
    },
    onError: (err) => setFlash({ kind: 'error', text: errDetail(err) }),
  })
  const sendBackMut = useMutation({
    mutationFn: ({ linkId, body }) => sendBackSmartKraalTagLink(ranchId, linkId, body),
    onSuccess: (out) => {
      const n = out.promotion?.retracted ?? 0
      setFlash({ kind: 'ok', text: `Sent back tag ${out.link.p_idtag} on ${animalName(out.animal)}.${n ? ` ${n} SmartKraal weight${n === 1 ? '' : 's'} withheld until the farm answers.` : ''}` })
      setSendingBack(null); setActionError(''); refresh()
    },
    onError: (err) => setActionError(errDetail(err)),
  })
  const createMut = useMutation({
    mutationFn: (body) => createAnimalFromSmartKraalTag(ranchId, body),
    onSuccess: (out) => {
      setFlash({ kind: out.promotion?.error ? 'warn' : 'ok', text: `Registered ${animalName(out.animal)} (${out.animal.rfid_tag}) from tag ${out.link.p_idtag}.${promoNote(out)}`, link: `/cattle/${out.animal.id}` })
      setCreating(null); setActionError(''); refresh()
    },
    onError: (err) => setActionError(errDetail(err) + (errCode(err) === 'rfid_exists' ? ' Use “Find animal” to bind it.' : '')),
  })
  const retireMut = useMutation({
    mutationFn: ({ linkId, body }) => retireSmartKraalTagLink(ranchId, linkId, body),
    onSuccess: (out) => {
      const n = out.promotion?.retracted ?? 0
      const err = out.promotion?.error ? ` Its weights could not be re-linked just now (${out.promotion.error}); the nightly sync retries.` : ''
      setFlash({ kind: err ? 'warn' : 'ok', text: `Retired the binding of tag ${out.link.p_idtag}.${n ? ` ${n} SmartKraal weight${n === 1 ? '' : 's'} removed from ${animalName(out.animal)}.` : ''}${err}` })
      setRetiring(null); setActionError(''); refresh()
    },
    onError: (err) => setActionError(errDetail(err)),
  })
  const busy = bindMut.isPending || createMut.isPending || retireMut.isPending || confirmMut.isPending || sendBackMut.isPending

  if (counts.error) return <DataLoadError error={counts.error} title="Could not load the registration queue" onRetry={counts.refetch} />
  if (!counts.data) return <div className="animate-pulse h-32 card" />
  if (counts.data.configured === false) {
    return (
      <div className="space-y-4">
        <PageHeader title="Register at the crush" icon={Tag} subtitle="Not configured for this ranch" />
        <p className="text-earth-600">The SmartKraal feed is set up for the pilot farm only.</p>
      </div>
    )
  }
  const c = counts.data
  const items = queue.data?.items || []
  const total = queue.data?.total || 0

  return (
    <div className="space-y-6">
      <Link to="/cattle" className="inline-flex items-center text-earth-500 hover:text-earth-700">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Cattle
      </Link>
      <PageHeader
        title="Register at the crush"
        icon={Tag}
        subtitle={`Which animal wears which SmartKraal tag · data set as of ${formatEAT(queue.data?.data_as_of_utc)}`}
        actions={(
          <>
            <a href={smartKraalTagLinksCsvUrl(ranchId)} className="btn-secondary" download>
              <Download className="h-4 w-4 mr-2" /> Export bindings (CSV)
            </a>
            <Link to="/cattle/smartkraal" className="btn-secondary"><Scale className="h-4 w-4 mr-2" /> Sync health</Link>
          </>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Tags seen" value={c.seen} icon={Tag} color="primary" />
        <StatCard title="Unbound" value={c.unbound} subtitle={`${c.weighed_unbound} with weights · ${c.proposed} with a proposal`} icon={Camera} color="amber" />
        <StatCard title="Awaiting review" value={c.bound} subtitle={c.flagged ? `${c.flagged} flagged by the checks` : 'bound by the farm'} icon={CheckCircle2} color={c.flagged ? 'amber' : 'blue'} />
        <StatCard title="Confirmed" value={c.confirmed} subtitle={c.flagged_confirmed ? `${c.flagged_confirmed} flagged since review` : undefined} icon={ShieldCheck} color="green" />
        <StatCard title="Sent back" value={c.sent_back} icon={AlertTriangle} color={c.sent_back ? 'red' : 'primary'} />
      </div>

      {flash && (
        <div className={`card p-4 flex items-start gap-3 ${flash.kind === 'error' ? 'border-red-200 bg-red-50' : flash.kind === 'warn' ? 'border-amber-200 bg-amber-50' : 'border-primary-200 bg-primary-50'}`} data-testid="flash">
          {flash.kind === 'error' ? <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            : flash.kind === 'warn' ? <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            : <CheckCircle2 className="h-5 w-5 text-primary-600 mt-0.5" />}
          <div className="text-sm flex-1">
            {flash.text}
            {flash.link && <Link to={flash.link} className="ml-2 text-primary-700 underline">Open animal</Link>}
          </div>
          <button type="button" onClick={() => setFlash(null)} className="text-earth-400 hover:text-earth-600" aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {emptyHerd && (
        <div className="card p-4 border-primary-200 bg-primary-50 text-sm text-primary-900 flex items-start gap-3" data-testid="empty-herd-banner">
          <ClipboardList className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">No animal in the register yet.</div>
            Register the herd at the crush: with census mode on, each animal is entered at the race as it is tagged
            (tag number, EID if it wears one, a short profile) and dated on the farm since the herd baseline date.
            Fill the rest of each profile afterwards under Profiles.
          </div>
        </div>
      )}

      <div className="border-b border-earth-200 flex flex-wrap gap-1">
        {TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-primary-600 text-primary-800' : 'border-transparent text-earth-500 hover:text-earth-700'}`}>
            {t.label}
            {t.count && t.count(c) != null && <span className="ml-2 rounded-full bg-earth-100 px-2 py-0.5 text-xs text-earth-700">{t.count(c)}</span>}
          </button>
        ))}
      </div>

      {tab === 'tagging' ? (
        <TaggingDay ranchId={ranchId} canWrite={canWrite} census={census} onCensusChange={setCensusMode}
                    profile={profile.data} profileLoading={profile.isLoading}
                    onBound={(out) => { refresh(); if (out.promotion?.error || (!census && out.warnings?.includes('tag_not_seen'))) setFlash({ kind: 'warn', text: said(out, 'Bound') }) }}
                    onRegistered={(out) => { refresh(); if (out.promotion?.error) setFlash({ kind: 'warn', text: `Registered ${animalName(out.animal)} from tag ${out.link.p_idtag}.${promoNote(out)}` }) }}
                    onNewAnimal={({ rfid, pIdtag }) => { setActionError(''); setCreating({ rfid, pIdtag }) }} />
      ) : tab === 'profiles' ? (
        <ProfilesTab ranchId={ranchId} canWrite={canWrite}
                     onSaved={(out) => {
                       if (out.error) { setFlash({ kind: 'error', text: out.error }); return }
                       const left = out.profile_incomplete
                       setFlash({ kind: out.errors ? 'warn' : 'ok',
                                  text: `${out.applied} profile${out.applied === 1 ? '' : 's'} updated${out.unchanged ? `, ${out.unchanged} unchanged` : ''}${out.errors ? `, ${out.errors} not saved (see the row)` : ''}.${left != null ? ` ${left} still incomplete.` : ''}` })
                     }}
                     onAssignEid={(a) => { setActionError(''); setAssigning(a) }} />
      ) : tab === 'onboarding' ? (
        <OnboardingTab ranchId={ranchId} />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-3 text-earth-400" />
              <input value={q} onChange={e => setQ(e.target.value)} className="input pl-9" placeholder="Tag number, EID, name, farm number…" data-testid="queue-search" />
            </div>
            {(tab === 'review' || tab === 'confirmed') && canReview && (() => {
              const pool = tab === 'review' ? c.bound : c.confirmed
              const nFlag = tab === 'review' ? c.flagged : c.flagged_confirmed
              const tenPct = Math.min(200, Math.max(1, Math.ceil(pool * 0.1)))
              return (
                <div className="flex flex-wrap items-center gap-2" data-testid="review-toolbar">
                  <button type="button" onClick={() => { setFlaggedOnly(v => !v); setOffset(0) }}
                          className={flaggedOnly ? 'btn-primary' : 'btn-secondary'} data-testid="only-flagged">
                    <Flag className="h-4 w-4 mr-2" /> Only flagged{nFlag ? ` (${nFlag})` : ''}
                  </button>
                  {sample ? (
                    <button type="button" onClick={() => setSample(null)} className="btn-secondary" data-testid="show-all">Show all {pool}</button>
                  ) : (
                    <button type="button" onClick={() => drawSample(tabDef.status, tenPct)}
                            className="btn-secondary" disabled={!pool || drawing} data-testid="review-sample">
                      {drawing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shuffle className="h-4 w-4 mr-2" />} Review a random 10% ({tenPct})
                    </button>
                  )}
                </div>
              )
            })()}
            <div className="text-sm text-earth-500">
              {queue.isFetching ? 'Loading…'
                : sample ? `your sample: ${sample.tags.length} tag${sample.tags.length === 1 ? '' : 's'}, ${items.filter(i => i.status === 'confirmed').length} confirmed, ${items.filter(i => i.status === 'sent_back').length} sent back`
                : `${total} tag${total === 1 ? '' : 's'}`}
              {!sample && total > PAGE && ` · showing ${offset + 1}–${Math.min(offset + PAGE, total)}`}
            </div>
            {!sample && total > PAGE && (
              <div className="flex gap-1">
                <button type="button" className="btn-secondary px-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" className="btn-secondary px-2" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>

          {queue.error ? (
            <DataLoadError error={queue.error} title="Could not load tags" onRetry={queue.refetch} />
          ) : ((tab === 'review' || tab === 'confirmed') && canReview) || tab === 'sent_back' ? (
            items.length === 0 && !queue.isFetching ? (
              <div className="card p-10 text-center text-earth-500">
                <ShieldCheck className="h-10 w-10 mx-auto text-primary-500 mb-2" />
                {tab === 'sent_back' ? 'Nothing sent back. Every binding the reviewer looked at stands.'
                  : flaggedOnly ? 'No binding is flagged by the checks.'
                  : tab === 'confirmed' ? 'Nothing confirmed yet.' : 'Nothing awaits review.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="review-grid">
                {items.map(card => (
                  <ReviewCard key={card.p_idtag} card={card} canReview={canReview} canWrite={canAnswer} busy={busy}
                              onConfirm={(cd) => { setActionError(''); confirmMut.mutate({ linkId: cd.link.id }) }}
                              onSendBack={(cd) => { setActionError(''); setSendingBack(cd) }}
                              onResubmit={(cd) => { if (!cd.animal) return; setActionError(''); bindMut.mutate({ body: { p_idtag: cd.p_idtag, animal_id: cd.animal.id, bind_method: 'photo_match' } }) }}
                              onRebind={(cd) => { setActionError(''); setPicker({ ...cd, replaceSentBack: true }) }}
                              onRetire={(cd) => { setActionError(''); setRetiring(cd) }} />
                ))}
              </div>
            )
          ) : tab === 'unbound' ? (
            items.length === 0 && !queue.isFetching ? (
              <div className="card p-10 text-center text-earth-500">
                <CheckCircle2 className="h-10 w-10 mx-auto text-primary-500 mb-2" />
                {dq ? `No unbound tag matches “${dq}”.` : 'Every tag the kraal has seen is bound to an animal.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="unbound-grid">
                {items.map(card => (
                  <TagCard key={card.p_idtag} card={card} canWrite={canWrite} busy={busy}
                           onConfirm={(cd) => { setActionError(''); bindMut.mutate({ body: { p_idtag: cd.p_idtag, animal_id: cd.proposal.animal.id, bind_method: 'proposal_accepted', proposal_basis: cd.proposal.basis } }) }}
                           onFind={(cd) => { setActionError(''); setPicker(cd) }}
                           onNew={(cd) => { setActionError(''); setCreating({ card: cd }) }} />
                ))}
              </div>
            )
          ) : (
            <LinksTable items={items} canWrite={canWrite} loading={queue.isFetching}
                        onRetire={(row) => { setActionError(''); setRetiring(row) }}
                        onFind={(cd) => { setActionError(''); setPicker(cd) }}
                        onNew={(cd) => { setActionError(''); setCreating({ card: cd }) }} />
          )}
        </>
      )}

      {picker && (
        <AnimalPicker ranchId={ranchId} card={picker} busy={bindMut.isPending} error={actionError}
                      onClose={() => { setPicker(null); setActionError('') }}
                      onPick={(a) => bindMut.mutate({ body: { p_idtag: picker.p_idtag, animal_id: a.id, bind_method: 'photo_match',
                                                              replace_sent_back: !!picker.replaceSentBack } })}
                      onNew={() => { setCreating({ card: picker }); setPicker(null); setActionError('') }} />
      )}
      {sendingBack && (
        <SendBackForm row={sendingBack} busy={sendBackMut.isPending} error={actionError}
                      onClose={() => { setSendingBack(null); setActionError('') }}
                      onSubmit={(body) => sendBackMut.mutate({ linkId: sendingBack.link.id, body })} />
      )}
      {creating && (
        <NewAnimalForm ranchId={ranchId} card={creating.card} pIdtag={creating.pIdtag} rfid={creating.rfid}
                       busy={createMut.isPending} error={actionError}
                       onClose={() => { setCreating(null); setActionError('') }}
                       onSubmit={(body) => createMut.mutate(body)} />
      )}
      {retiring && (
        <RetireForm row={retiring} busy={retireMut.isPending} error={actionError}
                    onClose={() => { setRetiring(null); setActionError('') }}
                    onSubmit={(body) => retireMut.mutate({ linkId: retiring.link.id, body })} />
      )}
      {assigning && (
        <AssignEidModal ranchId={ranchId} animal={assigning} onClose={() => setAssigning(null)}
                        onDone={(out) => {
                          setAssigning(null)
                          setFlash({ kind: 'ok', text: out.changed ? `EID ${out.rfid_tag} assigned to ${animalName(out.animal)}, replacing ${out.previous_rfid_tag}.`
                            : `${animalName(out.animal)} already wears EID ${out.rfid_tag}.`, link: `/cattle/${out.animal.id}` })
                          refresh()
                          queryClient.invalidateQueries({ queryKey: ['animal', out.animal.id] })
                        }} />
      )}
    </div>
  )
}
